import { Catalog } from './types';

/**
 * Spanish catalog — the source of truth for wording. Every key that exists
 * here must exist in `catalog.en.ts`; the pair is checked by
 * `catalog.test.ts` so a new string can never ship half-translated.
 *
 * `label.*` keys back the legacy `getLabel(id)` helper, which is still called
 * from ~80 places. Keeping the ids stable means those call sites became
 * bilingual without being touched.
 */
export const es: Catalog = {
  'label.alreadyReserved':
    'Lo sentimos. La estación que intentas reservar, ya fue reservada por otra persona. Intenta con otra estación u otra fecha distinta.',
  'label.duplicateReservationWarning':
    'Ya tienes una reservación para este día. Si necesitas reservar otro espacio, cancela primero la reservación actual.',
  'label.fullCapacity':
    'Lo sentimos, la capacidad aceptada para este día ya ha sido completada. Intenta con otro día.',
  'label.reservationSuccessMessage':
    'Reservación realizada satisfactoriamente. Puedes encontrar la información de tu nueva reservación en la página de "Mis reservaciones", en la sección de "Próximas reservaciones"',
  'label.reservationErrorMessage':
    'Lo sentimos, hubo un error y no pudimos guardar tu reservación, intenta de nuevo.',
  'label.lightOccupation': 'Ocupación estable',
  'label.mediumOccupation': 'Acercándose a la ocupación máxima',
  'label.maxOccupation': 'Ocupación máxima alcanzada',
  'label.floorOccupationChangedSuccess': 'El aforo permitido ha sido cambiado con éxito.',
  'label.floorOccupationChangedError':
    'Lo sentimos. No se pudo cambiar el aforo permitido. Intenta de nuevo.',
  'label.cancelReservationMessageFailed':
    'Lo sentimos, hubo un error y no pudimos cancelar tu reservación, intenta de nuevo.',
  'label.cancelReservationMessageSuccess': 'Reservación cancelada con éxito.',
  'label.changeWorkstationMessageSuccess': 'Estación cambiada con éxito.',
  'label.changeWorkstationMessageFailed':
    'Lo sentimos, no se pudo cambiar la estación. Puede que ya esté ocupada. Intenta con otra.',
  'label.editLunchSuccess': 'Almuerzo actualizado con éxito.',
  'label.editLunchFailed': 'No se pudo actualizar el almuerzo. Intentá de nuevo.',
  'label.createUserMessageSuccess': 'Registro completado con éxito.',
  'label.createUserMessageFailed': 'El registro no pudo ser completado, intenta de nuevo.',
  'label.checkInMessageFailed':
    'Lo sentimos, hubo un error y no pudimos realizar el check-in, intenta de nuevo.',
  'label.checkInMessageSuccess': 'Check-in realizado con éxito.',
  'label.checkOutMessageFailed':
    'Lo sentimos, hubo un error y no pudimos realizar el check-out, intenta de nuevo.',
  'label.checkOutMessageSuccess': 'Check-out realizado con éxito.',
  'label.sameDayCutoff':
    'No es posible realizar reservaciones para el mismo día después de las 4:00 p.m.',
  'label.maxBookingDateHoverMessage': 'Solo podés reservar hasta',
  'label.bookingWindowPolicyHint':
    'Podés reservar la semana en curso y la siguiente. Cada lunes a las 12:00 a. m. (hora CR) se habilita una semana más.',
  'label.footerNeedHelp': '¿Necesitás ayuda?',
  'label.footerCopyright': 'WPP Enterprise Solutions Costa Rica',
  'label.lunchQrTitle': 'Tu código QR de almuerzo',
  'label.lunchQrInstructions': 'Presenta este código al retirar tu almuerzo',
  'label.lunchQrNameLabel': 'Almuerzo de',
  'label.lunchDelivered': 'Almuerzo ya entregado',
  'label.lunchPending': 'Pendiente de retirar',
  'label.lunchNoReservation': 'Reservá tu estación para obtener tu código de almuerzo',
  'label.lunchNotRequested': 'No solicitaste almuerzo para hoy',
  'label.lunchSurveyCta': '¿Cómo estuvo tu almuerzo?',
  'label.lunchSurveyCtaSubtitle': 'Tu opinión nos ayuda a mejorar el servicio',
  'label.lunchSurveyButton': 'Calificar almuerzo',
  'label.lunchSurveyTitle': 'Califica tu almuerzo',
  'label.lunchSurveySubtitle': 'Selecciona de 1 a 5 estrellas según tu experiencia de hoy',
  'label.lunchSurveyCommentLabel': 'Comentarios (opcional)',
  'label.lunchSurveyCommentLabelRequired': 'Comentarios (obligatorio)',
  'label.lunchSurveyCommentRequiredHint':
    'Cuéntanos qué podemos mejorar para completar tu calificación.',
  'label.lunchSurveyCommentPlaceholder': 'Cuéntanos qué te pareció el almuerzo de hoy...',
  'label.lunchSurveySubmit': 'Enviar calificación',
  'label.lunchSurveyCancel': 'Ahora no',
  'label.lunchSurveyThanks': '¡Gracias por tu opinión!',
  'label.lunchSurveyDone': 'Ya calificaste tu almuerzo de hoy',
  'label.lunchSurveyError': 'No se pudo enviar tu calificación. Intenta de nuevo.',

  // --- Language selector -------------------------------------------------
  'language.label': 'Idioma',
  'language.aria': 'Cambiar idioma',
  'language.description': 'Elegí el idioma de la aplicación. Se guarda en tu cuenta.',
  'language.saveError': 'No pudimos guardar tu idioma. Se aplicó en este dispositivo.',
  'language.lockedHint': 'Esta sección solo está disponible en español: PROCOMER no admite documentación en otro idioma.',

  // --- Header ------------------------------------------------------------
  'header.openMenu': 'Abrir menú',
  'header.closeMenu': 'Cerrar menú',
  'header.logout': 'Salir',
  'header.myProfileTitle': 'Ver mi perfil',
  'header.viewProfileOf': 'Ver el perfil de {name}',
  'header.app.deskbooking': 'Deskbooking',
  'header.app.assets': 'Control de Activos',
  'header.app.reports': 'Reportes',
  'header.app.admin': 'Administración',
  'header.app.profile': 'Mi Perfil',
  'header.app.skills': 'Skill Matrix',

  // --- Navigation --------------------------------------------------------
  'nav.deskbooking': 'Deskbooking',
  'nav.hint.newReservation': 'Elige tu espacio en el mapa',
  'nav.hint.myReservations': 'Haz check-in, revisa o cancela',
  'nav.newReservation': 'Nueva reservación',
  'nav.myReservations': 'Mis reservaciones',
  'nav.adminReservation': 'Reservación administrativos',
  'nav.admin': 'Admin',
  'nav.assets': 'Control de Activos',
  'nav.reports': 'Reportes',
  'nav.skills': 'Skill Matrix',
  'nav.logout': 'Salir',

  // --- Profile page ------------------------------------------------------
  'profile.loadError': 'No pudimos cargar tu perfil: {message}',
  'profile.upcoming.one': '{count} reservación próxima',
  'profile.upcoming.other': '{count} reservaciones próximas',
  'profile.assets.one': '{count} activo a mi nombre',
  'profile.assets.other': '{count} activos a mi nombre',
  'profile.tab.activity': 'Actividad',
  'profile.tab.skills': 'Mi perfil profesional',
  'profile.card.attendance': 'Asistencias',
  'profile.card.week': 'Semana',
  'profile.card.month': 'Mes',
  'profile.card.year': 'Año',
  'profile.card.attendanceDesc': 'Días con reservación activa',
  'profile.card.topWeekday': 'Día más frecuente',
  'profile.card.topWeekdayVisits.one': '{count} visita en los últimos 12 meses',
  'profile.card.topWeekdayVisits.other': '{count} visitas en los últimos 12 meses',
  'profile.card.topWeekdayEmpty': 'Aún no hay suficientes visitas',
  'profile.card.lateCancels': 'Cancelaciones tardías',
  'profile.card.lateCancelsDesc': '{total} en total · {month} este mes',
  'profile.card.myAssets': 'Activos a mi nombre',
  'profile.card.myAssetsDesc': 'Equipo bajo tu responsabilidad',
  'profile.assetsDisclaimer':
    'El inventario se sigue cargando durante el próximo mes, así que el total puede estar incompleto.',
  'profile.panel.monthly': 'Asistencia mensual',
  'profile.panel.monthlyDesc': 'Días con reservación activa en los últimos 12 meses',
  'profile.panel.daysThisYear': '{count} días este año',
  'profile.chartTooltip':
    '{label}: {attended} asistencias · {lunches} almuerzos · {lateCancels} cancelaciones tardías',
  'profile.preferences.title': 'Preferencias',
  'profile.preferences.desc': 'Ajustes que se guardan en tu cuenta y te siguen a cualquier dispositivo',
  'profile.preferences.languageHint':
    'Detectamos el idioma de tu computadora la primera vez. Podés cambiarlo cuando querás.',
  // --- Profile page: charts, tables, empty states ------------------------
  'profile.chart.previousMonths': 'Meses anteriores',
  'profile.chart.currentMonth': 'Mes en curso',
  'profile.panel.lateLunch': 'Cancelaciones tardías de almuerzo',
  'profile.panel.lateLunchDesc': 'Almuerzos cancelados después de las 2:30 p. m. del día anterior',
  'profile.panel.totalCount': '{count} en total',
  'profile.lateCancels.emptyTitle': 'Sin cancelaciones tardías',
  'profile.lateCancels.emptyDesc': 'Gracias por avisar a tiempo cuando no vas a almorzar.',
  'profile.table.lunchDate': 'Fecha del almuerzo',
  'profile.table.cancelledOn': 'Cancelado el',
  'profile.table.status': 'Estado',
  'profile.table.lateStatus': 'Fuera de tiempo',
  'profile.pagination.previous': 'Anterior',
  'profile.pagination.next': 'Siguiente',
  'profile.pagination.range': '{from}–{to} de {total}',
  'profile.panel.assets': 'Activos a mi nombre',
  'profile.panel.assetsDesc': 'Equipo y mobiliario asignado a tu persona',
  'profile.panel.assetsBadge': '{count} activos',
  'profile.assets.emptyTitle': 'Sin activos asignados',
  'profile.assets.emptyDesc':
    'Si tienes equipo de la oficina a tu cargo, contacta a la persona encargada del control de activos.',
  'profile.table.plate': 'Placa',
  'profile.table.category': 'Categoría',
  'profile.table.brandModel': 'Marca / Modelo',
  'profile.table.serial': 'Número de serie',
  'profile.table.location': 'Ubicación',
  'profile.asset.myHome': 'Mi casa',
  // --- Map / booking page --------------------------------------------------
  'map.title.new': 'Nueva Reservación',
  'map.title.change': 'Cambiar Estación',
  'map.subtitle.new': 'Seleccioná una fecha y estación para reservar',
  'map.subtitle.change': 'Seleccioná una nueva estación para tu reservación',
  'map.exitEditMode': 'Salir del modo edición',
  // --- Booking dialog & workstation map ------------------------------------
  'common.yes': 'sí',
  'common.no': 'no',
  'common.yesCap': 'Sí',
  'common.noCap': 'No',
  'common.cancel': 'Cancelar',
  'common.confirm': 'Confirmar',
  'common.accept': 'Entendido',
  'extraLunch.deleteConfirm':
    '¿Eliminar la asignación de almuerzo extra para {name}?',
  'common.date': 'Fecha',
  'common.link': 'link',
  'booking.detailTitle': 'Detalle de reservación',
  'booking.stationLine': 'Estación: {station}',
  'booking.dateLine': 'Fecha: {date}',
  'booking.parkingQuestion': '¿Requiere espacio de parqueo?',
  'booking.parkingPolicyBefore':
    'Los espacios de parqueo son limitados, por favor lea nuestra ',
  'booking.parkingPolicyLink': 'Política de Parqueos',
  'booking.parkingPolicyAfter':
    ', si usted utiliza el parqueo del sótano, por favor seleccione la opción "No"',
  'booking.lunchTitle': 'Almuerzo',
  'booking.lunchUnclaimed': 'No retirado',
  'noShowLunch.title': 'Tu almuerzo se canceló a las 12md',
  'noShowLunch.body': 'No registramos tu ingreso a la oficina antes del mediodía. Si ya llegaste, podés recuperarlo.',
  'noShowLunch.cta': 'Reactivar mi almuerzo',
  'noShowLunch.success': '¡Listo! Tu almuerzo quedó reactivado.',
  'noShowLunch.error': 'No pudimos reactivar tu almuerzo. Intentá de nuevo.',
  'noShowLunch.blocked.PICKUP_CLOSED': 'El servicio de almuerzo ya cerró (2:00 p.m.).',
  'noShowLunch.blocked.LUNCH_REASSIGNED': 'Tu almuerzo ya se le entregó a otra persona.',
  'noShowLunch.blocked.NO_LUNCH_LEFT': 'Ya no quedan almuerzos disponibles para hoy.',
  'noShowLunch.blocked.DESK_TAKEN': 'Otra persona reservó tu espacio. Reservá otro espacio para hoy y volvé aquí para recuperar tu almuerzo.',
  'noShowLunch.blocked.ALREADY_HAS_LUNCH': 'Tu reserva de hoy ya incluye almuerzo.',
  'lunchQr.fullScreen': 'Mostrar en pantalla completa',
  'lunchQr.fullScreenHint': 'Subí el brillo de la pantalla y acercá el código a la cámara.',
  'lunchQr.close': 'Cerrar',
  'booking.lunchChecking': 'Consultando disponibilidad de almuerzo...',
  'booking.lunchNoFriday': 'No hay servicio de almuerzo los viernes.',
  'booking.lunchDayDisabled': 'No hay servicio de almuerzo este día.',
  'booking.lunchCutoff': 'Reserva cerrada: el límite es 2:30 PM del {day} anterior.',
  'booking.lunchCutoffFriday': 'viernes',
  'booking.lunchCutoffDay': 'día',
  'booking.lunchQuestion': '¿Requiere almuerzo?',
  'booking.lunchUnavailable': '(No hay disponibilidad)',
  'booking.lunchTimeLabel': 'Horario de almuerzo',
  'booking.slotUnavailable': ' (No disponible)',
  'booking.dietaryPreferences': 'Preferencias dietéticas',
  'booking.vegetarian': 'Vegetariano',
  'booking.lunchPolicyBefore':
    'Para garantizar que haya almuerzo para todos, es indispensable realizar la reserva a través de Deskbooking. La misma debe hacerse antes de las 2:30 PM del día anterior. Revisa los lineamientos en este ',
  'booking.changeStationTitle': 'Cambiar estación',
  'booking.changeStationQuestion':
    'Ya tenés una reservación para este día. ¿Deseás cambiar tu estación a',
  'booking.changeStationKeepsData': 'Tu almuerzo y demás datos se mantienen sin cambios.',
  'booking.changeAction': 'Cambiar',
  'booking.cancelTitle': 'Cancelar reservación',
  'booking.cancelQuestion': '¿Estás seguro de que deseas cancelar la reservación?',
  'booking.checkInQuestion':
    '¿Estás seguro de que deseas realizar el check-in para la reservación?',
  'booking.checkOutQuestion':
    '¿Estás seguro de que deseas realizar el check-out para la reservación?',
  'booking.howItWorks': 'Cómo funciona',
  'booking.hintHighTables': 'Las filas BC y DE son mesas altas y no poseen pantalla ni teclado',
  'booking.hintOccupied': 'Hacé clic en una estación ocupada (roja) para ver quién la reservó',
  'booking.emptyTitle': 'Seleccioná una estación',
  'booking.emptySubtitle': 'Hacé clic en una estación disponible del mapa para reservarla',
  'booking.stationReserved': 'Estación reservada:',
  'booking.stationSelected': 'Estación seleccionada:',
  'booking.stationAria': 'Estación {station}',
  'booking.verifying': 'Verificando...',
  'booking.currentStation': 'Estación actual',
  'booking.confirmChange': 'Confirmar cambio',
  'booking.reserve': 'Reservar',
  'booking.frequent': 'Frecuentes',
  'booking.frequentTooltip': '{label} · {times} reservas en los últimos 90 días',
  'booking.frequentTakenToday': ' · ocupada este día',
  // --- My reservations (today / upcoming) ----------------------------------
  'common.edit': 'Editar',
  'reservations.todayTitle': 'Reservación de hoy',
  'reservations.todayEmpty': 'No tenés una reservación para hoy.',
  'reservations.upcomingTitle': 'Próximas reservaciones',
  'reservations.upcomingEmpty': 'No tenés próximas reservaciones.',
  'reservations.colStation': 'Estación/Sala',
  'reservations.colDate': 'Fecha',
  'reservations.colNeedsLunch': 'Requiere almuerzo',
  'reservations.colLunchTime': 'Hora de Almuerzo',
  'reservations.colLunchTimeMobile': 'Hora de almuerzo',
  'reservations.stationPrefix': 'Estación',
  'reservations.autoCheckIn': 'Check-in automático',
  'reservations.autoCheckOut': 'Check-out automático',
  'reservations.lunchDeliveredAt': 'Tu almuerzo ya fue entregado',
  'reservations.lunchDeliveredAtTime': ' a las {time}',
  'reservations.lunchDeliveredNoQr': '. No es necesario presentar el código QR.',
  'reservations.lunchScheduleLine': 'Horario: {time}',
  'reservations.lunchTimeJoin': ' a ',
  'reservations.extraLunchAvailable.one':
    'Hay {count} almuerzo disponible para este día por cancelaciones tardías. Si necesitás uno, contactá al equipo de RRHH para que te lo asignen.',
  'reservations.extraLunchAvailable.other':
    'Hay {count} almuerzos disponibles para este día por cancelaciones tardías. Si necesitás uno, contactá al equipo de RRHH para que te lo asignen.',
  // --- Reservation row actions ---------------------------------------------
  'reservations.editSeat': 'Editar Asiento',
  'reservations.editLunch': 'Editar Almuerzo',
  // --- Reservations page, check-in, lunch edit, filters --------------------
  'common.save': 'Guardar',
  'common.success': 'Operación exitosa',
  'common.error': 'Error',
  'myReservations.title': 'Mis Reservaciones',
  'myReservations.subtitle': 'Consultá tus reservaciones actuales e historial',
  'checkIn.detailTitle': 'Detalle del Check In',
  'checkIn.stationLabel': 'Estación:',
  'checkIn.dateLabel': 'Fecha:',
  'occupation.title': 'Ocupación',
  'occupation.current': 'Ocupación actual:',
  'occupation.max': 'Ocupación máxima esperada:',
  'editLunch.title': 'Editar Almuerzo',
  'editLunch.dayDisabled': 'No hay servicio de almuerzo el día {date}.',
  'editLunch.cutoffPassed':
    'Ya pasó la hora límite (2:30 PM del día anterior) para modificar el almuerzo.',
  'editLunch.lateChangeWarning':
    'Ya pasó la hora límite (2:30 PM del día anterior). El cambio se registrará como modificación tardía.',
  'editLunch.vegan': 'Vegano',
  'filter.selectPrefix': 'Seleccionar',
  'filter.workstation': 'estación de trabajo',
  'filter.meetingRoom': 'sala de reuniones',
  'filter.workstationCard': 'Estación de trabajo',
  'workstations.stationLabel': 'Estación {station}',
  'workstations.reservationCount.one': '{count} Reservación',
  'workstations.reservationCount.other': '{count} Reservaciones',
  'events.type.celebration': 'Celebración',
  'events.type.general': 'General',
  // --- Event banner types --------------------------------------------------
  'events.type.connect': 'Connect / All Hands',
  // --- Reservation history & QR shortcut pages -----------------------------
  'common.exit': 'Salir',
  'common.information': 'Información',
  'history.empty': 'No tenés reservaciones en este mes.',
  'shortcut.notYours': 'Este usuario no presenta dicha reservación',
  'shortcut.tooEarly': 'Aún no se encuentra en la fecha disponible para su check-in',
  'shortcut.alreadyCheckedIn': 'Ya se realizó el check-in a dicha reservación',
  'shortcut.notFound': 'No existe dicha reservación',
  'shortcut.checkInUnavailable': 'El check-in manual ya no está disponible desde este acceso.',
  'shortcut.cancelFailed': 'Error al cancelar la reservación.',
  'shortcut.loadFailed': 'Error al cargar la información de la reservación.',
  'shortcut.notFoundDot': 'No existe dicha reservación.',
  'shortcut.notYoursDot': 'Este usuario no presenta dicha reservación.',
  'shortcut.alreadyProcessed': 'La reservación ya ha sido procesada.',
  'shortcut.signInRequired': 'Por favor, inicie sesión para acceder a esta página.',
  // --- Map legend, popover and sidebar -------------------------------------
  'map.legendTitle': 'Simbología',
  'map.legend.available': 'Disponible',
  'map.legend.occupied': 'Ocupada',
  'map.legend.unavailable': 'No disponible',
  'map.legend.selected': 'Seleccionada',
  'map.imageAlt': 'Mapa del edificio',
  'map.favoriteTooltip': '{station} · una de tus estaciones frecuentes',
  'map.occupiedBy': 'Ocupado por',
  'map.external': 'Externo',
  'map.allDay': 'Todo el día',
  'map.hourAria': 'hora',
  'map.occupancyTitle': 'Ocupación general',
  'map.spaceOccupancy': 'Ocupación de espacios',
  'common.selectOption': 'Seleccione una opción',
  // --- Floor plan labels (drawn inside the map) ----------------------------
  'map.floorPlan.hallway': 'Pasillo',
  'map.floorPlan.mainHallwayLine1': 'Pasillo',
  'map.floorPlan.mainHallwayLine2': 'Principal',
  'map.floorPlan.emergencyExitLine1': 'Salida de',
  'map.floorPlan.emergencyExitLine2': 'Emergencia',
  // --- Login, talent gate and the what's-new modal ------------------
  'common.understood': 'Entendido',
  'common.selectDate': 'Seleccioná una fecha',
  'login.retrying': 'Ocurrió un problema al iniciar sesión. Reintentando automáticamente...',
  'login.failed': 'No se pudo iniciar sesión. Por favor vuelve a intentarlo.',
  'talent.soon.badge': 'Próximamente',
  'talent.soon.text':
    'Estamos afinando los últimos detalles del Skill Matrix. Muy pronto vas a poder completar tu perfil técnico, mantener tus certificaciones al día y ver qué sabe hacer tu equipo.',
  'talent.soon.note':
    'Te avisamos apenas se habilite. Por ahora no tenés que hacer nada.',
  'talent.noProfile':
    'No tenés un perfil técnico asociado. Si creés que es un error, escribile a un administrador.',
  'whatsNew.title': 'Novedades en Deskbooking',
  'whatsNew.subtitle': 'Esto es lo que agregamos este mes',
  'whatsNew.comingSoon': 'En progreso',
  'whatsNew.profile.title': 'Tu perfil',
  'whatsNew.profile.desc':
    'Entrá desde tu nombre en la barra superior: ahí ves tus asistencias por semana, mes y año, tu día más frecuente en la oficina y tus próximas reservaciones.',
  'whatsNew.brand.title': 'Nueva identidad WPP',
  'whatsNew.brand.desc':
    'Deskbooking se actualizó a la marca WPP Enterprise Solutions: nueva tipografía, colores y logo.',
  'whatsNew.mobile.title': 'Mejoras en el celular',
  'whatsNew.mobile.desc':
    'Menú desplegable, mapa centrado automáticamente y listas de reservaciones rediseñadas para pantallas pequeñas.',
  'whatsNew.lunchHistory.title': 'Historial de almuerzos',
  'whatsNew.lunchHistory.desc':
    'Revisá desde tu perfil las cancelaciones tardías de almuerzo, con la fecha y hora de cada una.',
  'whatsNew.assets.title': 'Activos a mi nombre',
  'whatsNew.assets.desc':
    'Consultá el equipo bajo tu responsabilidad. Seguimos cargando el inventario durante el próximo mes.',
  // --- Skill Matrix: a person's own profile --------------------------------
  'skills.since.today': 'hoy',
  'skills.since.yesterday': 'ayer',
  'skills.since.days': 'hace {count} días',
  'skills.since.months.one': 'hace {count} mes',
  'skills.since.months.other': 'hace {count} meses',
  'skills.since.years.one': 'hace {count} año',
  'skills.since.years.other': 'hace {count} años',
  'skills.profileNotFound': 'No se encontró el perfil.',
  'skills.freelance': 'Freelance',
  'skills.inactive': 'Inactivo',
  'skills.field.email': 'Email',
  'skills.field.languages': 'Idiomas',
  'skills.field.experience': 'Años de experiencia',
  'skills.field.registered': 'Skills registradas',
  'skills.advancedCount': '· {count} en avanzado',
  'skills.noAccount': 'Sin cuenta en Deskbooking',
  'skills.noAccountTitle':
    'No tiene cuenta en Deskbooking, así que no puede editar su perfil. Lo mantiene un administrador.',
  'skills.saving': 'Guardando…',
  'skills.saved': 'Guardado',
  'skills.notSaved': 'No se guardó',
  'skills.hideLegend': 'Ocultar explicación',
  'skills.showLegend': '¿Qué significa cada nivel?',
  'skills.addSkill': '+ Agregar skill',
  'skills.removeFromProfile': 'Quitar de mi perfil',
  'skills.emptyOwn':
    'Marcá el nivel de las tecnologías con las que trabajás. Las que dejés en “—” no aparecerán en tu perfil, y podés volver a traerlas cuando quieras desde “Agregar skill”.',
  'skills.emptyOther': 'Esta persona aún no ha registrado skills.',
  'skills.saveErrorTail':
    '— tus cambios siguen en pantalla; volvé a tocar un nivel para reintentar.',
  'skills.noIssuer': 'Sin emisor',
  'skills.neverExpires': 'No vence',
  'skills.expired': 'Vencida',
  'skills.daysShort': '{count} d',
  'skills.project': 'Proyecto',
  'skills.ongoing': 'En curso',
  'skills.level.basic': 'Básico',
  'skills.level.intermediate': 'Intermedio',
  'skills.level.advanced': 'Avanzado',
  // --- Skill Matrix: team directory ----------------------------------------
  'skills.searchTeam': 'Buscar por nombre o skill…',
  'skills.clear': 'Limpiar',
  'skills.typeToSearch': 'Escribí {count} letras para buscar',
  'skills.peopleCount.one': '{count} colaborador',
  'skills.peopleCount.other': '{count} colaboradores',
  'skills.noMatches': 'Nadie coincide con esa búsqueda.',
  'skills.moreCount': '+{count} más',
  // --- Skill Matrix: editors -----------------------------------------------
  'skills.save': 'Guardar',
  'skills.close': 'Cerrar',
  'skills.add': 'Agregar',
  'skills.required': 'Nombre *',
  'skills.err.nameRequired': 'El nombre es obligatorio.',
  'skills.err.expiryBeforeIssue':
    'La fecha de vencimiento no puede ser anterior a la de emisión.',
  'skills.err.years': 'Los años de experiencia deben ser un entero entre 0 y 60.',
  'skills.err.positionRequired': 'El puesto es obligatorio.',
  'skills.cert.edit': 'Editar certificación',
  'skills.cert.new': 'Nueva certificación',
  'skills.cert.issuer': 'Emisor',
  'skills.cert.issued': 'Emitida',
  'skills.cert.expires': 'Vence',
  'skills.cert.credential': 'Credencial o URL',
  'skills.cert.relatedSkill': 'Skill relacionada',
  'skills.cert.none': 'Ninguna',
  'skills.cert.noExpiryHint': 'Dejá "Vence" vacío si la certificación no expira.',
  'skills.project.edit': 'Editar proyecto',
  'skills.project.new': 'Agregar proyecto',
  'skills.project.nameLabel': 'Proyecto *',
  'skills.project.role': 'Rol en el proyecto',
  'skills.project.start': 'Inicio',
  'skills.profile.edit': 'Editar perfil',
  'skills.profile.seniority': 'Seniority',
  'skills.profile.undefined': 'Sin definir',
  'skills.profile.contract': 'Contrato',
  'skills.profile.linkedAccount': 'Cuenta enlazada',
  'skills.profile.active': 'Perfil activo',
  'skills.profile.position': 'Puesto *',
  'skills.profile.removeLanguage': 'Quitar idioma',
  'skills.profile.addLanguage': '+ Agregar idioma',
  'skills.profile.aboutMe': 'Sobre mí',
  'skills.profile.aboutMePlaceholder':
    'Visible para todo el equipo. En qué te gusta trabajar, qué podés ayudar a resolver, qué estás aprendiendo…',
  'skills.profile.github': 'Usuario de GitHub',
  'skills.profile.githubPlaceholder': 'Opcional. Tu usuario o el enlace a tu perfil',
  'skills.profile.comments': 'Comentarios',
  'skills.profile.commentsPlaceholder': 'Contexto adicional, intereses, disponibilidad…',
  'skills.add.title': 'Agregar una skill',
  'skills.add.hint':
    'Agregá únicamente tecnologías con las que ya hayas trabajado. Se suman con nivel {level} y podés ajustarlo después; si no la conocés, es mejor dejarla fuera.',
  'skills.add.searchLabel': 'Buscar',
  'skills.add.searchPlaceholder': 'Terraform, Contentful, Snowflake…',
  'skills.add.typeToSearch': 'Escribí para buscar en el catálogo.',
  'skills.add.noneInCatalog':
    'No hay nada con ese nombre en el catálogo. Pedile a un administrador de skills que la agregue.',
  'skills.add.notFound':
    'No encontramos «{term}» en el catálogo. Podés agregarla vos y queda disponible para el resto.',
  'skills.add.category': 'Categoría',
  'skills.add.creating': 'Agregando…',
  'skills.add.createAndAdd': 'Crear "{term}" y agregarla',
  // --- Skill Matrix: first-time skills form (gates the module) -------------
  'nav.skillsPending': 'Te faltan tus skills',
  'skills.ob.title': 'Contanos qué manejás',
  'skills.ob.intro':
    'Son unas 16 skills, las de tu rol. Se guarda solo a medida que respondés, '
    + 'así que podés dejarlo a medias y seguir después.',
  'skills.ob.roleQuestion': '¿Cuál de estos roles se parece más a tu día a día?',
  'skills.ob.roleHelp':
    'Elegí el que más se acerque. Define qué skills te preguntamos, y un '
    + 'administrador lo ajusta después si hace falta.',
  'skills.ob.rolesMulti': 'Podés elegir más de uno si hacés dos cosas.',
  'skills.ob.stepOf': 'Paso {step} de {total}',
  'skills.ob.back': 'Atrás',
  'skills.ob.finish': 'Terminar',
  'skills.ob.gateTitle': 'Antes de entrar',
  'skills.ob.savedNote': 'Se guarda solo. Podés cerrar y seguir después.',
  'skills.ob.emptyGrid':
    'Tu rol todavía no tiene skills en la grilla. Avisale a un administrador '
    + 'de skills para que la configure.',
  'skills.ob.noRoles':
    'Todavía no hay roles configurados. Avisale a un administrador de skills.',
  'skills.ob.reviewNote':
    'Cualquier nivel lo podés cambiar después desde tu perfil.',
  'skills.ob.bonusTitle': 'IA — suma aparte',
  'skills.ob.bonusIntro':
    'Esto no es de ningún rol en particular: sepas o no, no cambia lo que se '
    + 'espera de tu puesto. Suma, así que vale la pena que quede anotado.',
  'skills.ob.continueAnyway': 'Entrar igual al módulo',
  'skills.ob.dontKnow': 'Dejá en — lo que no manejes. No pasa nada.',
  'skills.dir.stale': 'Desactualizado',
  'skills.dir.staleCount': '{count} respondieron hace más de {months} meses',
  'skills.dir.reAskStale': 'Volver a preguntarles',
  'skills.dir.reAskStaleConfirm':
    '¿Volver a pedirle el formulario a las {count} personas que respondieron hace '
    + 'más de {months} meses? No se borra ningún nivel: ven lo que cargaron y lo confirman.',
  'skills.dir.reAskRole': 'Volver a preguntar a este rol',
  'skills.dir.reAskRoleConfirm':
    '¿Volver a pedirle el formulario a todos los de {role}? Se usa después de '
    + 'cambiarle la grilla al rol. No se borra ningún nivel.',
  'skills.dir.reAskAll': 'Volver a preguntar a todos',
  'skills.dir.reAskAllConfirm':
    '¿Volver a pedirle el formulario a las {count} personas que ya lo completaron? '
    + 'Es el refresco anual. No se borra ningún nivel.',
  'skills.dir.reAskDone': 'Listo: {count} personas van a ver el formulario otra vez.',
  'skills.ob.roleMismatch': 'Ninguno describe bien lo que hago',
  'skills.ob.roleMismatchHelp':
    'Elegí igual el que más se acerque para poder seguir. Le avisamos a un '
    + 'administrador para que cree el rol que te falta.',
  'skills.dir.mismatchHint': 'Sus puestos en HR sugieren: {list}',
  'skills.dir.mismatch': 'Rol aproximado',
  'skills.dir.mismatchCount': '{count} dicen que ningún rol les calza',
  'skills.dir.seeMismatch': 'Ver quiénes',
  'skills.dir.seeMismatchOff': '← Ver a todos',
  'skills.dir.mismatchTitle':
    'Eligieron el rol más cercano, no el suyo. Asignarles roles a mano quita este aviso.',
  'skills.dir.formState': 'Formulario',
  'skills.dir.formStateLabel': 'Filtrar por estado del formulario',
  'skills.dir.anyFormState': 'Todo estado',
  'skills.dir.onlyPending': 'Solo pendientes',
  'skills.dir.onlyDone': 'Solo completados',
  'skills.dir.done': 'Completo',
  'skills.dir.pending': 'Pendiente',
  'skills.dir.halfway': 'En progreso',
  'skills.dir.rollout': '{done} de {total} completaron sus skills',
  'skills.dir.notStarted': '{count} no lo abrieron',
  'skills.dir.seePending': 'Ver solo pendientes',
  'skills.dir.seeEveryone': '← Ver a todos',
  'skills.dir.resetAsk': 'Pedir actualización',
  'skills.dir.resetTitle':
    'Le vuelve a pedir el formulario. Los niveles que ya cargó no se borran.',
  'skills.dir.resetConfirm':
    '¿Pedirle a {name} que complete el formulario de nuevo? No se borra ningún nivel.',
  'skills.bonusScopeOn': '★ Ver solo las plus',
  'skills.bonusScopeOff': '← Salir de las plus',
  'skills.bonusScopeTitle':
    'Skills que se le preguntan a todos, sin importar el rol. Combinable con un rol.',
  'skills.bonus.title': 'Skills plus (para todos)',
  'skills.bonus.intro':
    'Se les preguntan a todos, sin importar el rol, y van en una sección aparte '
    + 'del formulario. Quitar una no borra ningún nivel ya cargado.',
  'skills.bonus.search': 'Buscar una skill para agregar…',
  'skills.bonus.add': 'Agregar',
  'skills.bonus.remove': 'Quitar',
  'skills.bonus.removeTitle':
    'Deja de preguntársela a todos. Los niveles ya cargados quedan intactos.',
  'skills.newInGrid':
    'Se agregaron {count} skills a la grilla de tu rol desde que completaste el '
    + 'formulario. Respondelas acá y listo.',
  // --- Skill Matrix: job roles (what decides each person's grid) -----------
  'skills.allRoles': 'Todos los roles',
  'skills.anyRole': 'Todo rol',
  'skills.role.filterLabel': 'Filtrar por rol',
  'skills.col.missing': 'Sin cubrir',
  'skills.col.missingOf': 'Sobre {expected} personas en alcance',
  'skills.gap.title': 'Skills que el rol pide y nadie declaró',
  'skills.gap.tag': 'nadie',
  'skills.gap.count': '○ {count} sin cubrir',
  'skills.gap.seeAll': '← Ver todas',
  'skills.profile.roles': 'Roles',
  'skills.profile.rolesHelp':
    'Definen qué skills se le piden: la grilla es la unión de los roles '
    + 'marcados. Sin ninguno se muestra el catálogo completo.',
  'skills.profile.role': 'Rol',
  'skills.profile.roleHelp':
    'Define qué skills se le piden. Sin rol se muestra el catálogo completo.',
  'skills.profile.noRole': 'Sin rol',
  'skills.tab.roles': 'Roles y grilla',
  'skills.roles.title': 'Roles y su grilla de skills',
  'skills.roles.intro':
    'Cada rol define qué skills se le piden a quien lo tiene. Un QA no debería '
    + 'ver la grilla de back-end: no aporta y ensucia el reporte de cobertura.',
  'skills.roles.newRole': 'Nuevo rol',
  'skills.roles.name': 'Nombre',
  'skills.roles.nameEn': 'Nombre en inglés',
  'skills.roles.nameEnHelp': 'Opcional. Vacío usa el nombre en español.',
  'skills.roles.description': 'Descripción',
  'skills.roles.descriptionEn': 'Descripción en inglés',
  'skills.roles.active': 'Activo',
  'skills.roles.skillCount': '{count} skills en la grilla',
  'skills.roles.editGrid': 'Editar grilla',
  'skills.roles.gridFor': 'Grilla de {role}',
  'skills.roles.gridNote':
    'Quitar una skill del rol no borra el nivel de nadie: queda en su perfil '
    + 'como skill agregada.',
  'skills.roles.searchSkills': 'Buscar skill…',
  'skills.roles.wholeCategory': 'Toda la categoría',
  'skills.roles.noneOfCategory': 'Ninguna',
  'skills.roles.selected': '{count} seleccionadas',
  'skills.roles.empty': 'Todavía no hay roles creados.',
  'skills.roles.nameRequired': 'Poné un nombre de al menos 2 caracteres.',
  'skills.roles.inactive': 'Inactivo',
  // --- Skill Matrix: coverage report, charts and directory -----------------
  'skills.allCategories': 'Todas las categorías',
  'skills.risk.title': 'Skills que dependen de una sola persona',
  'skills.risk.seeAll': '← Ver todas',
  'skills.risk.count': '⚠ {count} en riesgo',
  'skills.risk.onePerson': '1 persona',
  'skills.risk.onePersonTitle': 'Una sola persona declara esta skill',
  'skills.expiringTitle': 'Certificaciones por vencer',
  'skills.daysCount': '{count} días',
  'skills.col.skill': 'Skill',
  'skills.col.category': 'Categoría',
  'skills.col.distribution': 'Distribución',
  'skills.col.basicShort': 'Básico',
  'skills.col.interShort': 'Inter.',
  'skills.col.advShort': 'Avanz.',
  'skills.col.total': 'Total',
  'skills.seeCharts': 'Ver gráficos',
  'skills.chart.distribution': 'Distribución',
  'skills.chart.category': 'Categoría',
  'skills.chart.declare.one': 'persona declara esta skill.',
  'skills.chart.declare.other': 'personas declaran esta skill.',
  'skills.chart.blindSpot': ' Ninguna en nivel avanzado — puede ser un punto ciego.',
  'skills.chart.shapeOf': 'Forma de la categoría {category}.',
  'skills.chart.showingTop': ' Se muestran las {shown} con más gente de {total}.',
  'skills.dir.search': 'Buscar por nombre, puesto o email…',
  'skills.dir.anySkill': 'Cualquier skill',
  'skills.dir.level1': 'Nivel 1+ (básico)',
  'skills.dir.level2': 'Nivel 2+ (intermedio)',
  'skills.dir.level3': 'Nivel 3 (avanzado)',
  'skills.dir.anySeniority': 'Todo seniority',
  'skills.dir.bothContracts': 'FTE y freelance',
  'skills.dir.onlyFte': 'Solo FTE',
  'skills.dir.onlyFreelance': 'Solo freelance',
  'skills.dir.person': 'Colaborador',
  'skills.dir.level': 'Nivel',
  'skills.dir.certs': 'Certs',
  'skills.dir.contract': 'Contrato',
  'skills.dir.noResults': 'Ningún resultado con esos filtros.',
  // --- Skill Matrix: two the accent grep could not see ---------------------
  'skills.err.projectRequired': 'El proyecto es obligatorio.',
  'skills.experienceWith': 'Experiencia con',
  // --- Reports: shell, presets and comparison metrics ----------------------
  'reports.title': 'Reportes',
  'reports.forbidden.title': 'Acceso restringido',
  'reports.forbidden.body':
    'No tienes permisos para ver los Reportes. Si crees que esto es un error, contacta a un administrador.',
  'reports.preset.all': 'Historial',
  'reports.preset.last9': 'Últimos 9 días',
  'reports.preset.last30': 'Últimos 30 días',
  'reports.preset.last90': 'Últimos 3 meses',
  'reports.preset.thisMonth': 'Este mes',
  'reports.subtitle.assets': 'Resumen del inventario de activos y descarga en CSV.',
  'reports.subtitle.fullHistoryRange': 'Historial completo · del {from} al {to}',
  'reports.subtitle.range': 'Del {from} al {to}',
  'reports.subtitle.fullHistory': 'Historial completo',
  'reports.subtitle.noData': 'Sin datos en el rango seleccionado',
  'reports.previousDay': ' · día anterior',
  'reports.page.previous': 'Página anterior',
  'reports.page.next': 'Página siguiente',
  // Visit report spreadsheet (Admin dashboard and Reservations page)
  'export.sheetName': 'Reporte',
  'export.visits.name': 'Nombre Completo del colaborador',
  'export.visits.phone': 'Número de Teléfono',
  'export.visits.date': 'Fecha de visita de la oficina',
  'export.visits.checkin': '¿Realizó check-in para la reservación?',
  'export.visits.lunchTime': 'Horario de almuerzo',
  'reports.exportCsv': 'Exportar CSV',
  'reports.compareMonths': 'Comparar meses',
  'reports.compareGlobal': 'Comparación global del tab actual',
  'reports.metric.late': 'Cancelaciones tardías',
  'reports.metric.requested': 'Almuerzos solicitados',
  'reports.metric.ceded': 'Almuerzos cedidos',
  'reports.metric.responses': 'Respuestas de encuesta',
  'reports.metric.rating': 'Calificación promedio',
  'reports.metric.reservations': 'Reservas del mes',
  'reports.metric.attendees': 'Personas con reservas',
  'reports.loadError': 'No se pudo cargar el reporte',
  'reports.tryLater': 'Intenta de nuevo más tarde.',
  // --- Reports: the "in <period>" phrase appended to sentences -------------
  'reports.period.between': 'del {from} al {to}',
  'reports.period.since': 'desde {from}',
  'reports.period.until': 'hasta {to}',
  'reports.period.custom': 'en el periodo personalizado',
  'reports.period.allTime': 'en el historial completo',
  'reports.period.selected': 'en el periodo seleccionado',
  'reports.period.inPreset': 'en {preset}',
  // --- Reports: lunch tab --------------------------------------------------
  'reports.from': 'Desde',
  'reports.to': 'Hasta',
  'reports.exportTitle': 'Descarga todos los datos del reporte actual',
  'reports.clearSearch': 'Limpiar búsqueda',
  'reports.legend.q': '¿Qué es una cancelación tardía?',
  'reports.legend.a':
    'Es un almuerzo que se cancela después del corte (2:30 p.m. del día hábil anterior), cuando el pedido ya se envió al proveedor y la comida no se puede recuperar. Este reporte muestra únicamente cancelaciones tardías.',
  'reports.legend.importantLabel': ' Importante:',
  'reports.legend.startDate': 'los reportes de almuerzo inician desde el 20 de abril de 2026.',
  'reports.card.lunchReservations': 'Reservas con almuerzo',
  'reports.card.lunchReservationsDesc': 'Almuerzos reservados {period}.',
  'reports.card.lateCancels': 'Cancelaciones tardías',
  'reports.card.lateCancelsDesc': '{count} tardías {period}.',
  'reports.card.unclaimed': 'Almuerzos no retirados',
  'reports.card.unclaimedDesc': 'No retirados {period} (sin QR ni marcación manual).',
  'reports.card.lateRate': '% de cancelaciones tardías',
  'reports.card.lateRateDesc': '≈ {rate} de cada 100 reservas {period}.',
  'reports.byMonth.title': 'Cancelaciones tardías por mes',
  'reports.byMonth.desc': 'Mirá cómo cambian mes a mes',
  'reports.col.month': 'Mes',
  'reports.col.late': 'Tardías',
  'reports.col.comparison': 'Comparación',
  'reports.col.date': 'Fecha',
  'reports.col.day': 'Día',
  'reports.byDow.title': 'Cancelaciones tardías por día de la semana',
  'reports.byDow.desc': '{count} tardías {period}. Los viernes no hay almuerzo',
  'reports.lateSuffix': 'tardías',
  'reports.topDays.title': 'Días con mayor incidencia',
  'reports.topDays.desc': 'Las 10 fechas con más cancelaciones tardías',
  'reports.perPerson.title': 'Impacto por persona: tardías y no retirados',
  'reports.perPerson.desc':
    'Incluye cancelaciones tardías y almuerzos no retirados (reservaron, pero no usaron QR ni marcación manual en tablet). Buscá por nombre o correo.',
  // --- Reports: per-person detail and lunch orders -------------------------
  'reports.col.person': 'Persona',
  'reports.col.unclaimed': 'No retirados',
  'reports.col.detail': 'Detalle',
  'reports.sort.descending': ' descendente',
  'reports.sort.ascending': ' ascendente',
  'reports.sort.byLate': 'Ordenar por tardías{direction}',
  'reports.sort.byUnclaimed': 'Ordenar por no retirados{direction}',
  'reports.noPeople': 'No se encontraron personas',
  'reports.noPeopleFor': 'No se encontraron personas para "{term}"',
  'reports.loadingDetail': 'Cargando detalle...',
  'reports.noLateForPerson': 'No hay cancelaciones tardías para esta persona.',
  'reports.noUnclaimedForPerson': 'No hay almuerzos no retirados para esta persona.',
  'reports.detail.reservation': 'Reserva: {date}',
  'reports.detail.cancelled': 'Canceló: {time}',
  'reports.ceded.title': 'Almuerzos cedidos y no cedidos (perdidos) por mes',
  'reports.ceded.desc':
    'Basado en cancelaciones tardías. Cedidos: sobrantes reasignados. No cedidos (perdidos): sobrantes que no se lograron dar como extra',
  'reports.col.lateBase': 'Tardías (base)',
  'reports.col.ceded': 'Cedidos',
  'reports.col.notCeded': 'No cedidos (perdidos)',
  // --- Reports: lunch satisfaction survey ----------------------------------
  'reports.survey.title': 'Resultados de encuestas',
  'reports.survey.desc':
    'Calificación de satisfacción (1 a 5 estrellas) que dejan las personas después de recibir su almuerzo',
  'reports.survey.emptyTitle': 'Aún no hay respuestas',
  'reports.survey.emptyDesc':
    'Todavía no se han registrado encuestas de almuerzo en el rango seleccionado.',
  'reports.survey.average': 'Calificación promedio',
  'reports.survey.responseRate': '% que respondió',
  'reports.survey.responseRateDesc': 'de {count} almuerzos entregados {period}.',
  'reports.survey.distribution': 'Distribución de calificaciones',
  'reports.survey.distributionDesc': 'Cuántas personas dieron cada calificación',
  'reports.survey.byMonth': 'Satisfacción por mes',
  'reports.survey.byMonthDesc': 'Promedio de estrellas y número de respuestas por mes',
  'reports.col.average': 'Promedio',
  'reports.col.responses': 'Respuestas',
  'reports.survey.comments': 'Comentarios recientes',
  'reports.survey.commentsDesc': 'Opiniones dejadas por los usuarios al calificar su almuerzo',
  // --- Reports: deskbooking tab --------------------------------------------
  'reports.desk.legendLabel': 'Reservas',
  'reports.desk.legend':
    ': estaciones reservadas en la oficina. Cada reserva equivale a una visita registrada. Estos indicadores se calculan con reservas activas (confirmada, check-in o check-out), no con presencia física validada.',
  'reports.desk.reservations': 'Reservas',
  'reports.desk.reservationsDesc': 'Estaciones reservadas {period}.',
  'reports.desk.dailyAverage': 'Promedio diario de reservas',
  'reports.desk.dailyAverageDesc': 'reservas por día en promedio {period}.',
  'reports.desk.peakDay': 'Día con más reservas',
  'reports.desk.peakDayDesc': '{count} reservas ({dow}) {period}.',
  'reports.desk.topDays': 'Días con más reservas',
  'reports.desk.topDaysDesc': 'Las 10 fechas con más estaciones reservadas',
  'reports.desk.byDow': 'Reservas por día de la semana',
  'reports.desk.byDowDesc': 'Descubrí qué días la oficina se llena más',
  'reports.desk.byPerson': 'Reservas por persona y mes',
  'reports.desk.byPersonDesc':
    'Cantidad de días con reserva por persona en un mismo mes. Buscá por nombre o correo',
  'reports.col.days': 'Días',
  'reports.desk.noAttendance': 'Personas sin asistencia por mes',
  'reports.desk.noAttendanceDesc':
    'Personas activas que no registraron reservas en el mes seleccionado',
  'reports.searchPerson': 'Buscar persona…',
  // --- Reports: CSV export headers and section names -----------------------
  'reports.csv.tab': 'Tab',
  'reports.csv.section': 'Seccion',
  'reports.csv.field': 'Campo',
  'reports.csv.value': 'Valor',
  'reports.csv.detail': 'Detalle',
  'reports.csv.tabLunch': 'Almuerzos',
  'reports.csv.tabDesk': 'Deskbooking',
  'reports.csv.summary': 'Resumen',
  'reports.csv.lateRatePlain': '% cancelaciones tardías',
  'reports.csv.cancelsByMonth': 'Cancelaciones por mes',
  'reports.csv.cancelsByDay': 'Cancelaciones por día',
  'reports.csv.orders': 'Pedidos',
  'reports.csv.totalRequested': 'Total solicitados',
  'reports.csv.totalCeded': 'Total cedidos',
  'reports.csv.ordersByMonth': 'Pedidos por mes',
  'reports.csv.survey': 'Encuesta',
  'reports.csv.totalResponses': 'Total respuestas',
  'reports.csv.totalDelivered': 'Total entregados',
  'reports.csv.averageRating': 'Promedio calificación',
  'reports.csv.responseRate': 'Tasa respuesta',
  'reports.csv.surveyDistribution': 'Distribución encuesta',
  'reports.csv.stars': '{count} estrellas',
  'reports.csv.surveyByMonth': 'Encuesta por mes',
  'reports.csv.monthComparison': 'Comparación de meses',
  // --- Asset Control: list, filters and table ------------------------------
  'common.previousArrow': '← Anterior',
  'common.nextArrow': 'Siguiente →',
  'common.pageOf': 'Página {page} de {total}',
  'assets.list.title': 'Lista de activos',
  'assets.list.count': '({count} activos)',
  'assets.antiquity.all': 'Antigüedad: Todos',
  'assets.antiquity.lessThan1': 'Antigüedad: Menos de 1 año',
  'assets.antiquity.oneToThree': 'Antigüedad: 1 a 3 años',
  'assets.antiquity.threeToFive': 'Antigüedad: 3 a 5 años',
  'assets.antiquity.fiveToTen': 'Antigüedad: 5 a 10 años',
  'assets.antiquity.tenPlus': 'Antigüedad: Más de 10 años',
  'assets.filter.categoryAll': 'Categoría: Todos',
  'assets.filter.category': 'Categoría: {name}',
  'assets.filter.locationAll': 'Ubicación: Todos',
  'assets.filter.locationOffice': 'Ubicación: Oficina',
  'assets.filter.locationHome': 'Ubicación: Casa Colaborador',
  'assets.filter.vatAll': 'IVA: Todos',
  'assets.filter.vatYes': 'IVA: Sí',
  'assets.filter.vatNo': 'IVA: No',
  'assets.filter.locationChip': 'Ubicación:',
  'assets.filter.clearLocation': 'Quitar filtro de ubicación',
  'assets.loadError': 'Error al cargar activos: {message}',
  'assets.col.plate': 'Placa',
  'assets.col.brand': 'Marca',
  'assets.col.serialModelLine1': 'Número de serie /',
  'assets.col.serialModelLine2': 'Modelo',
  'assets.col.category': 'Categoría',
  'assets.col.assignedTo': 'Asignado a',
  'assets.col.purchaseDateLine1': 'Fecha de',
  'assets.col.purchaseDateLine2': 'Compra',
  'assets.col.vat': 'IVA',
  'assets.col.actions': 'Acciones',
  'assets.col.date': 'Fecha',
  'assets.homeOf': 'Casa de {name}',
  'assets.employee': 'colaborador',
  // --- Asset Control: search, detail and change log ------------------------
  'assets.noPermission':
    'No tienes permisos para acceder al Control de Activos. Si crees que es un error, contacta a un administrador.',
  'assets.search.placeholderShort': 'Buscar nombre o número de activo',
  'assets.search.placeholder': 'Buscar por número de activo o nombre de persona',
  'assets.search.errorTitle': 'No pudimos buscar',
  'assets.search.errorText':
    'Ocurrió un error al consultar los activos. Revisa tu conexión e inténtalo de nuevo.',
  'assets.search.typeMoreTitle': 'Escribe un poco más',
  'assets.search.typeMoreText':
    'Ingresa al menos 2 caracteres para buscar por número de activo, marca, modelo, serie o nombre de la persona asignada.',
  'assets.search.noResultsTitle': 'Sin resultados',
  'assets.search.noResultsBefore': 'No encontramos activos para',
  'assets.search.noResultsAfter':
    '. Revisa la escritura o intenta con el número de placa, la marca o el nombre de la persona.',
  'assets.detail.loadError': 'Error al cargar el activo: {message}',
  'assets.detail.plate': 'Número de placa',
  'assets.detail.serial': 'Número de serie',
  'assets.detail.model': 'Modelo',
  'assets.detail.location': 'Ubicación',
  'assets.detail.purchaseDate': 'Fecha de compra',
  'assets.detail.vatPaid': 'Pago de IVA',
  'assets.detail.noImage': 'Sin imagen',
  'assets.detail.byUser': '· por {email}',
  'assets.detail.deleteTitle': 'Eliminar activo',
  'assets.detail.deleteConfirm':
    '¿Eliminar el activo con placa #{plate}? Esta acción no se puede deshacer.',
  'assets.field.plate': 'Placa',
  'assets.field.name': 'Nombre',
  'assets.field.custody': 'Tipo de ubicación',
  'assets.field.paysVat': 'Pagó IVA',
  'assets.field.photo': 'Foto',
  'assets.action.create': 'Activo creado',
  'assets.action.update': 'Activo actualizado',
  'assets.action.delete': 'Activo eliminado',
  'assets.list.empty': 'No se encontraron activos con los filtros actuales.',
  // --- Asset Control: form, reports, map and roles -------------------------
  'assets.form.locationOfAsset': 'Ubicación del activo',
  'assets.form.selectCategory': 'Seleccione una categoría',
  'assets.form.officeLocation': 'Ubicación',
  'assets.form.selectOfficeLocation': 'Seleccione la ubicación',
  'assets.form.brandRequired': 'Introduzca una marca',
  'assets.form.searchPerson': 'Buscar por nombre o correo',
  'assets.form.photo': 'Fotografía',
  'assets.form.dropImage': 'Arrastre y suelte la imagen aquí o haga clic para cargarla',
  'assets.form.takePhoto': 'Tomar foto con la cámara',
  'assets.form.savedButPhotoFailed':
    'El activo se guardó correctamente, pero la imagen no se pudo subir.',
  'assets.reports.title': 'Reportes',
  'assets.reports.subtitle': 'Resumen del inventario de activos y descarga en CSV.',
  'assets.reports.downloadCsv': 'Descargar inventario en CSV',
  'assets.reports.total': 'Total de activos',
  'assets.reports.withVat': 'Con IVA',
  'assets.reports.withoutVat': 'Sin IVA',
  'assets.reports.withPhoto': 'Con fotografía',
  'assets.reports.byCategory': 'Activos por categoría',
  'assets.reports.byLocation': 'Activos por ubicación',
  'assets.reports.noData': 'Sin datos.',
  'assets.reports.count': 'Cantidad',
  'assets.map.hint': 'Desliza el mapa para explorarlo y toca una zona para ver sus activos.',
  'assets.roles.restricted': 'Acceso restringido',
  'assets.roles.superAdminOnly': 'Solo SUPER_ADMIN puede gestionar roles.',
  'assets.roles.title': 'Gestión de Roles',
  'assets.roles.subtitle': 'Asignar y revocar permisos de administración por email.',
  'assets.roles.assign': 'Asignar rol',
  'assets.roles.userEmail': 'Email del usuario',
  'assets.roles.filter': 'Filtrar por email o rol',
  'assets.roles.colRole': 'Rol',
  'assets.roles.colAssignedOn': 'Asignado el',
  'assets.roles.revokeTitle': 'Revocar rol',
  'assets.roles.revokeConfirm': '¿Revocar el rol',
  // --- Asset Control: photo upload dialog ----------------------------------
  'assets.form.formats': 'Formatos compatibles: PNG, JPG.',
  'assets.form.uploadFailedTitle': 'No pudimos subir la imagen',
  'assets.form.uploadFailedBody':
    'El activo se guardó correctamente, pero la imagen no se pudo subir. ¿Deseas guardar el activo sin imagen o reintentar la subida?',
  // --- Admin: dashboard ----------------------------------------------------
  'admin.occupancy': 'Ocupación VML',
  'admin.occupancyMap': 'Ocupación VML MAP',
  'admin.enabledStations': 'Estaciones habilitadas',
  'admin.noServiceToday': 'Sin servicio hoy',
  'admin.reusedAvailable': 'Reutilizados: {reused} · Disponibles: {available}',
  'admin.sendLateReportTitle': 'Enviar reporte de cancelaciones por correo',
  'admin.sending': 'Enviando…',
  'admin.stationBlocking': 'Bloqueo de estaciones',
  'admin.stationBlockingDesc':
    'El bloqueo de una estación es permanente: no depende de la fecha seleccionada y se mantiene hasta que se habilite de nuevo.',
  'admin.doubleClickHint': 'Doble clic en una estación para bloquearla o habilitarla',
  'admin.blockStation': 'Bloquear estación',
  'admin.blockConfirm': '¿Deseas bloquear la estación {station}?',
  // --- Admin: dashboard (paired forms) -------------------------------------
  'admin.enableStation': 'Habilitar estación',
  'admin.enableConfirm': '¿Deseas habilitar la estación {station}?',
  'admin.sendReport': 'Enviar reporte por correo',
  // --- Admin: settings -----------------------------------------------------
  'admin.settings': 'Configuración',
  'admin.occupancyLimit': 'Límite máximo de ocupación',
  'admin.searchStation': 'Buscar estación...',
  'admin.allStations': 'Todas',
  'admin.enabledOnes': 'Habilitadas',
  'admin.disabledOnes': 'Deshabilitadas',
  // --- Admin: asset categories ---------------------------------------------
  'admin.cat.loadError': 'No se pudieron cargar las categorías.',
  'admin.cat.title': 'Categorías de activos',
  'admin.cat.desc':
    'Administra las categorías de activos. Desactivar una categoría la oculta al crear activos, sin afectar los activos existentes.',
  'admin.cat.new': 'Nueva categoría',
  'admin.cat.englishName': 'Nombre en inglés (opcional)',
  'admin.cat.englishShort': 'En inglés (opcional)',
  'admin.cat.add': 'Agregar categoría',
  'admin.cat.empty': 'Aún no hay categorías.',
  // --- Admin: CSV asset import ---------------------------------------------
  'admin.import.title': 'Importar activos (CSV)',
  'admin.import.intro':
    'Sube un archivo CSV para crear varios activos a la vez. Si hay placas repetidas (en el archivo o ya existentes) no se importa ninguna fila.',
  'admin.import.columnsIntro': 'Columnas:',
  'admin.import.noneImported':
    'No se importó ningún activo. Corrige estas filas y vuelve a intentar:',
  // --- Admin: CSV import column notes --------------------------------------
  'admin.import.required': 'requerida',
  'admin.import.requiredUse': 'requerida; use',
  'admin.import.forHomeAssets': 'para activos que están en casa de la persona',
  'admin.import.yesNo': 'Sí/No',
  'admin.import.assignedToNote':
    'correo o nombre completo; obligatorio si la ubicación es Casa Colaborador',
  'admin.import.mustMatch': 'Categoría y Ubicación deben coincidir con nombres existentes.',
  // --- Admin: day planning, closures, events and lunch days ----------------
  'admin.day.intro':
    'Planificá la operación de un día: cerrá la oficina por feriados o eventos especiales, y anunciá Connects, celebraciones o cualquier comunicado. Seleccioná un día en el calendario para ver y editar su detalle.',
  'admin.day.upcomingClosures': 'Próximos cierres de oficina',
  'admin.day.upcomingEvents': 'Próximos eventos',
  'admin.day.weekend': 'Fin de semana — la oficina permanece cerrada por defecto.',
  'admin.day.officeClosed': 'Oficina cerrada este día',
  'admin.day.reasonLabel': 'Razón:',
  'admin.day.closeOffice': 'Cerrar oficina este día',
  'admin.day.eventsOfDay': 'Eventos del día',
  'admin.day.noEvents': 'No hay eventos para este día.',
  'admin.day.reasonRequired': 'Razón (requerida)',
  'admin.day.reasonPlaceholder': 'Ej: Día feriado, cierre por mantenimiento...',
  'admin.day.eventTitle': 'Título',
  'admin.day.eventDescription': 'Descripción (opcional)',
  'admin.day.reasonOptional': 'Razón (opcional)',
  'admin.day.selectDayEvents':
    'Seleccioná un día en el calendario para ver sus eventos. Agregá un evento con doble clic.',
  'admin.day.selectDayDetail':
    'Seleccioná un día en el calendario para ver su detalle. Hacé doble clic para editarlo.',
  'admin.day.upcomingNoLunch': 'Próximos días sin almuerzo',
  'admin.day.fridayNoLunch': 'Viernes (sin almuerzo por defecto)',
  'admin.day.noLunchService': 'Sin servicio de almuerzo este día',
  'admin.day.noLunchReservations': 'No hay reservas de almuerzo para este día',
  // --- Admin: events and closures intros -----------------------------------
  'admin.events.intro':
    'Seleccioná un día en el calendario para ver sus eventos. Agregá un Connect (All Hands), una celebración o cualquier anuncio: se destacará en el calendario y aparecerá en el mapa para todos.',
  'admin.closures.intro':
    'Seleccioná un día en el calendario para ver su detalle. Hacé doble clic para marcar o desmarcar un cierre de oficina (feriado, evento especial, etc.).',
  // --- Admin: lunch-disabled days ------------------------------------------
  'admin.lunch.intro':
    'Seleccioná un día en el calendario para ver su detalle. Hacé doble clic para activar o desactivar el servicio de almuerzo.',
  'admin.lunch.timeOfDay': ' · {time} de {when}',
  'admin.lunch.today': 'hoy',
  'admin.lunch.yesterday': 'ayer',
  'admin.lunch.byPerson': 'por {name}',
  'admin.lunch.reasonPlaceholder': 'Ej: Feriado, evento especial...',
  // --- Admin: reservation history ------------------------------------------
  'admin.hist.user': 'Usuario',
  'admin.hist.editSeat': 'Editar asiento',
  'admin.hist.editLunch': 'Editar almuerzo',
  // --- Admin: create a reservation for somebody else -----------------------
  'admin.create.title': 'Reservar para alguien más',
  'admin.create.subtitle': 'Asigná una estación (y almuerzo) a un colaborador o visitante.',
  'admin.create.cta': 'Crear reservación',
  'admin.create.forWhom': '¿Para quién?',
  'admin.create.newVmlHint':
    'Si el correo VML no existe aún, se creará la cuenta automáticamente (su próximo ingreso con Okta funcionará).',
  'admin.create.visitorName': 'Nombre del visitante',
  'admin.create.visitorNoteBefore':
    'Se reservará como externo. No se crea ninguna cuenta y aparecerá en el mapa como “Ocupado por:',
  'admin.create.visitorNoteAfter': '(Externo)”.',
  'admin.create.namePlaceholder': 'Nombre',
  'admin.create.availableStation': 'Estación disponible',
  'admin.create.stationCount.one': '{count} estación disponible el {date}.',
  'admin.create.stationCount.other': '{count} estaciones disponibles el {date}.',
  // --- Admin: change station dialog ----------------------------------------
  'admin.change.currentStation': 'Estación actual:',
  'admin.change.noneAvailable': 'No hay estaciones disponibles ese día',
  'admin.change.newStation': 'Nueva estación',
  'admin.change.count.one':
    '{count} estación disponible. Se enviará un correo a la persona con la nueva estación.',
  'admin.change.count.other':
    '{count} estaciones disponibles. Se enviará un correo a la persona con la nueva estación.',
  // --- Admin: extra lunches ------------------------------------------------
  'admin.extra.empty':
    'Sin almuerzos extras asignados. Use "Asignar" cuando sobre un almuerzo por cancelación tardía, o para agregar un almuerzo adicional en un día lleno (antes de las 2:30 PM del día anterior).',
  'admin.extra.resendQr': 'Reenviar QR por email',
  'admin.extra.removeAssignment': 'Eliminar asignación',
  'admin.extra.dialogTitle': 'Asignar almuerzo extra',
  'admin.extra.dialogHint':
    'Asigne un almuerzo sobrante (por ejemplo, una reserva cancelada fuera de tiempo) a otra persona. Si el día está lleno y no hay sobrantes, puede agregar un almuerzo adicional siempre que aún haya tiempo de antelación (antes de las 2:30 PM del día anterior). Se enviará un email con el código QR al destinatario si se selecciona un usuario con correo.',
  'admin.extra.notInList': '¿No está en la lista? Ingrese el nombre manualmente:',
  'admin.extra.note': 'Nota (opcional)',
  'admin.extra.notePlaceholder': 'Ej: Sobró por cancelación tardía de Juan Pérez',
  'admin.extra.sendQr': 'Enviar QR por email al destinatario',
  // --- Admin: lunch reassignment and user management -----------------------
  'admin.reassign.hint':
    'El almuerzo ya fue ordenado. Al reasignarlo, su código QR deja de ser válido y la comida se entrega a la persona seleccionada.',
  'admin.reassign.notInList': '¿No aparece en la lista? Ingrese los datos manualmente.',
  'admin.reassign.sendQr': 'Enviar código QR por correo a la nueva persona',
  'admin.users.search': 'Buscar por nombre o correo...',
  'admin.users.allRoles': 'Todos',
  'admin.users.admins': 'Admins',
  'admin.users.users': 'Usuarios',
  'admin.users.noRole': 'Sin rol asignado',
  'admin.users.loadError': 'Error al cargar usuarios. Intentá de nuevo más tarde.',
  'admin.users.none': 'No se encontraron usuarios',
  'admin.users.lastSeenToday': 'Último acceso: hoy a las {time}',
  'admin.users.lastSeenOn': 'Último acceso: {date}, {time}',
  'admin.users.lastSeenNever': 'Sin accesos registrados',
  'admin.users.allPermissions': 'todos los permisos',
  'admin.users.allPermissionsTitle':
    'SUPER_ADMIN incluye todos los permisos, incluidos los que se agreguen después',
  'admin.users.makeAdmin': 'Hacer Admin',
  'admin.users.confirmTitle': 'Confirmar acción',
  'admin.users.confirmBody': '¿Estás seguro de que deseas {action} a {name}?',
  'admin.users.disableWarning':
    'Se cancelarán todas sus reservas futuras (estación y almuerzo) y se le impedirá el acceso al sistema.',
  'admin.users.revokeTitle': 'Revocar rol',
  'admin.users.revokeBody': '¿Revocar el rol {role} de {email}?',
  'admin.users.action.makeAdmin': 'promover a Admin',
  'admin.users.action.removeAdmin': 'quitar rol de Admin',
  'admin.users.action.disable': 'deshabilitar',
  'admin.users.action.enable': 'habilitar',
  'admin.users.action.editName': 'editar nombre',
  // --- Admin, assets and shared UI — bulk pass -----------------------------
  'common.delete': 'Eliminar',
  'common.next': 'Siguiente',
  'g.coverage': 'Cobertura',
  'g.certifications': 'Certificaciones',
  'g.downloadCsv': 'Descargar CSV',
  'g.activeOnly': 'Solo activas',
  'g.today': 'Hoy',
  'g.loading': 'Cargando...',
  'g.loadingEllipsis': 'Cargando…',
  'g.savingEllipsis': 'Guardando…',
  'g.deleting': 'Eliminando…',
  'g.importing': 'Importando…',
  'g.revoking': 'Revocando…',
  'g.uploadingImage': 'Subiendo imagen…',
  'g.required': 'Requerido',
  'g.requiredField': '*Campo requerido',
  'g.max50': 'Máximo 50 caracteres',
  'g.max100': 'Máximo 100 caracteres',
  'g.unexpectedError': 'Error inesperado',
  'g.unexpectedErrorLong': 'Ha ocurrido un error inesperado.',
  'g.noServerResponse': 'No se recibió respuesta del servidor.',
  'g.accessDenied': 'Acceso Denegado',
  'g.of': 'de',
  'g.nextArrow': 'Siguientes →',
  'g.wed': 'Mié',
  'g.sat': 'Sáb',
  'g.inactives': 'Inactivos',
  'g.inactiveParen': '(inactivo)',
  'g.pending': 'Pendiente',
  'g.notifications': 'Notificaciones',
  'g.nameOptional': 'Nombre (opcional)',
  'g.nameOrEmail': 'Nombre o email',
  'g.searchUser': 'Buscar usuario',
  'g.emailExample': 'usuario@vml.com',
  'g.emailPattern': 'nombre.apellido@vml.com',
  'g.assignedEmail': 'Correo asignado',
  'g.noFilterMatch': 'Ningún resultado coincide con el filtro.',
  'g.reservations': 'Reservaciones',
  'g.lunches': 'Almuerzos',
  'g.planning': 'Planificación',
  'g.users': 'Usuarios',
  'g.assets': 'Activos',
  'g.workstations': 'Estaciones',
  'g.reservation': 'Reservación',
  'g.booked': 'Reservada',
  'g.parking': 'Parqueo',
  'g.steady': 'Estable',
  'g.moderate': 'Moderada',
  'g.maximum': 'Máxima',
  'g.generateReport': 'Generar reporte',
  'g.adminStaff': 'Personal administrativo',
  'g.visitor': 'Visitante',
  'g.existingUser': 'Usuario existente',
  'g.otherVmlEmail': 'Otro (correo VML)',
  'g.includeLunch': 'Incluir almuerzo',
  'g.reassignLunch': 'Reasignar almuerzo',
  'g.reassigned': 'Reasignado',
  'g.markDelivered': 'Marcar como entregado',
  'g.markNotDelivered': 'Marcar como no entregado',
  'g.markVegetarian': 'Marcar como vegetariano',
  'g.vegetarianClickRemove': 'Vegetariano (clic para quitar)',
  'g.noLunchService': 'Sin servicio de almuerzo',
  'g.fridayNoLunch': 'Viernes — sin almuerzo',
  'g.lunchLower': 'almuerzo',
  'g.lunchesTotal': 'almuerzos en total',
  'g.assignedToLower': 'asignado a',
  'g.cancelledLower': 'cancelado',
  'g.userLower': 'usuario',
  'g.takenLower': 'ocupadas',
  'g.officeClosed': 'Oficina cerrada',
  'g.officeOpen': 'Oficina abierta — se permiten reservaciones',
  'g.closeOffice': 'Cerrar oficina',
  'g.reopenOffice': 'Reabrir oficina',
  'g.pastDate': 'Fecha pasada',
  'g.addEvent': '+ Agregar evento',
  'g.add': '+ Agregar',
  'g.deleteEvent': 'Eliminar evento',
  'g.eventTitleRequired': 'El título del evento es obligatorio.',
  'g.officeMap': 'Mapa de la oficina',
  'g.baseMonth': 'Mes base',
  'g.comparedMonth': 'Mes comparado',
  'g.comment': 'Comentario',
  'g.editName': 'Editar nombre',
  'g.nameUpdated': 'Nombre actualizado.',
  'g.assignedRoles': 'Roles asignados',
  'g.noRoles': 'Sin roles asignados.',
  'g.adminRoleRemoved': 'Rol de Admin removido.',
  'g.userPromoted': 'Usuario promovido a Admin.',
  'g.userEnabled': 'Usuario habilitado.',
  'g.userDisabled': 'Usuario deshabilitado. Sus reservas futuras fueron canceladas.',
  'g.roleAssignError': 'Error al asignar el rol.',
  'g.qrResent': 'QR reenviado.',
  'g.lunchReportSent': 'Reporte de almuerzo enviado exitosamente.',
  'g.reportSendFailed': 'No se pudo enviar el reporte.',
  'g.closureSaved': 'Cierre de oficina registrado exitosamente.',
  'g.closureSaveFailed': 'Error al registrar el cierre.',
  'g.closureDeleted': 'Cierre de oficina eliminado exitosamente.',
  'g.closureDeleteFailed': 'Error al eliminar el cierre.',
  'g.eventCreated': 'Evento creado exitosamente.',
  'g.eventCreateFailed': 'Error al crear el evento.',
  'g.eventDeleted': 'Evento eliminado exitosamente.',
  'g.eventDeleteFailed': 'Error al eliminar el evento.',
  'g.categoryCreateFailed': 'No se pudo crear la categoría.',
  'g.categoryUpdateFailed': 'No se pudo actualizar la categoría.',
  'g.saveFailed': 'No se pudo guardar el cambio.',
  'g.stationChangeFailed': 'No se pudo cambiar la estación.',
  'g.stationChanged': 'La estación se cambió correctamente.',
  'g.reservationCreated': 'Reservación creada correctamente.',
  'g.reservationCreateFailed':
    'No se pudo crear la reservación: la estación ya está ocupada o la persona ya tiene reserva ese día.',
  'g.pickUser': 'Seleccione un usuario.',
  'g.pickUserOrName': 'Seleccione un usuario o ingrese un nombre.',
  'g.pickPersonOrName': 'Seleccione una persona o ingrese un nombre.',
  'g.pickStation': 'Seleccione una estación disponible.',
  'g.enterVisitorName': 'Ingrese el nombre del visitante.',
  'g.enterVmlEmail': 'Ingrese un correo VML válido.',
  'g.addAsset': 'Agregar Activo',
  'g.editAsset': 'Editar Activo',
  'g.saveAsset': 'Guardar Activo',
  'g.saveChanges': 'Guardar cambios',
  'g.saveWithoutImage': 'Guardar sin imagen',
  'g.assetNotFound': 'Activo no encontrado.',
  'g.assetCreated': 'Activo creado exitosamente.',
  'g.assetUpdated': 'Activo actualizado exitosamente.',
  'g.assetCreateFailed': 'Error al crear el activo.',
  'g.assetUpdateFailed': 'Error al actualizar el activo.',
  'g.imageUploaded': 'Imagen subida exitosamente',
  'g.cameraDenied': 'Permiso de cámara denegado.',
  'g.cameraFailed': 'No se pudo acceder a la cámara.',
  'g.noChangesRecorded': 'Sin cambios registrados.',
  'g.noLocation': 'Sin ubicación',
  'g.requiredForHomeAssets': 'Requerido para activos en casa',
  'g.assetAtHomeNote':
    'El activo está en casa de la persona asignada, así que no lleva ubicación de oficina.',
  'g.assetInOfficeNote': 'El activo está dentro de la oficina, indique en cuál ubicación.',
  'g.searchingAssets': 'Buscando activos…',
  'g.searchingAssetsDetail': 'Revisando placas, marcas, series y personas asignadas.',
  'g.importFailed': 'No se pudo importar el archivo.',
  'g.fileReadFailed': 'No se pudo leer el archivo.',
  'g.templateFileName': 'plantilla-activos.xlsx',
  'g.purchaseDateLower': 'fecha de compra',
  'g.people': 'Colaboradores',
  'g.myTeam': 'Mi equipo',
  'g.currentProject': 'Proyecto actual',
  'g.noProject': 'Sin proyecto',
  'g.noProjectsRecorded': 'Sin proyectos registrados.',
  'g.noCertsRecorded': 'Sin certificaciones registradas.',
  'g.workingOn': 'Trabajando en',
  'g.updated': 'Actualizado',
  'g.added': 'Agregada',
  'g.backToPeople': '← Volver a colaboradores',
  'g.endBeforeStart': 'La fecha de fin no puede ser anterior a la de inicio.',
  'g.surveysCompleted': 'encuestas de almuerzo completadas  .',
  'loc.reception': 'Recepción',
  'loc.receptionEntrance': 'Recepción Entrada',
  'loc.receptionExit': 'Recepción Salida',
  'loc.entrance': 'Entrada',
  'loc.exit': 'Salida',
  'loc.printing': 'Impresión',
  'loc.printingArea': 'Área de Impresión',
  'loc.totoArea': 'Área ToTo',
  'loc.relaxArea': 'Área Relax',
  'loc.techManagement': 'Gerencia Tecnología',
  'loc.roomShower': 'Sala de Reunión The Shower',
  'loc.room506': 'Sala de Reunión 506',
  'loc.roomChurchill': 'Sala de Reunión Churchill',
  'loc.roomLaPulpe': 'Sala de Reunión La Pulpe',
  'loc.roomTuanis': 'Sala de Reunión Tuanis',
  'g.updatedOn': 'Actualizado el',
  'g.createdOn': 'Creado el',
  'g.administrative': 'Administrativos',
  'g.extraLunches': 'Almuerzos extras',
  'g.assigned': 'Asignado',
  'g.assigning': 'Asignando…',
  'g.assignmentRemoved': 'Asignación eliminada.',
  'g.extraLunchAssigned': 'Almuerzo extra asignado correctamente.',
  'g.lunchReassigned': 'Almuerzo reasignado correctamente.',
  'g.lunchDisabled': 'Almuerzo desactivado exitosamente.',
  'g.lunchEnabled': 'Almuerzo reactivado exitosamente.',
  'g.disableLunch': 'Desactivar almuerzo',
  'g.searchingAssetsShort': 'Buscando activos',
  'g.calculatingLunches': 'Calculando almuerzos disponibles',
  'g.uploadingImageShort': 'Subiendo imagen',
  'g.cancelledFem': 'Cancelada',
  'g.employeeHome': 'Casa Colaborador',
  'g.categoryCreated': 'Categoría creada.',
  'g.categoryUpdated': 'Categoría actualizada.',
  'g.categoryEnabled': 'Categoría activada.',
  'g.categoryDisabled': 'Categoría desactivada.',
  'g.officeClosures': 'Cierres de oficina',
  'g.peopleColon': 'Colaboradores:',
  'g.emailOptional': 'Correo (opcional)',
  'g.vmlEmail': 'Correo VML',
  'g.anyStatus': 'Cualquier estado',
  'g.downloadTemplate': 'Descargar plantilla',
  'g.disabled': 'Deshabilitado',
  'g.disabledPlural': 'Deshabilitados',
  'g.enabledFem': 'Habilitada',
  'g.fileNoRows': 'El archivo no tiene filas de datos.',
  'g.lunchSlotUnavailable': 'El horario de almuerzo seleccionado no está disponible.',
  'g.invalidEmail': 'Email inválido.',
  'g.workstationsTitle': 'Estaciones de Trabajo',
  'g.station1': 'Estación 1',
  'g.officeStatus': 'Estado de la oficina',
  'loc.opsManagement': 'Gerencia Operaciones',
  'g.reservationHistory': 'Historial de reservaciones',
  'g.adminLunchUpdateFailed': 'Error al actualizar almuerzo administrativo.',
  'g.stationUpdateFailed': 'Error al actualizar la estación.',
  'g.userUpdateFailed': 'Error al actualizar usuario.',
  'g.extraLunchFailed': 'Error al asignar almuerzo extra.',
  'g.lunchLoadFailed': 'Error al cargar datos de almuerzo.',
  'g.reservationFailed': 'Error al crear la reservación.',
  'g.lunchDisableFailed': 'Error al desactivar el almuerzo.',
  'g.lunchEnableFailed': 'Error al reactivar el almuerzo.',
  'g.userDisableFailed': 'Error al deshabilitar usuario.',
  'g.userEnableFailed': 'Error al habilitar usuario.',
  'g.deleteFailed': 'Error al eliminar.',
  'g.reportFailed': 'Error al enviar el reporte.',
  'g.qrSendFailed': 'Error al enviar los QR.',
  'g.reassignFailed': 'Error al reasignar el almuerzo.',
  'g.useVmlOption':
    'Ese es un correo VML. Use la opción "Otro (correo VML)" para reservar a nombre de un colaborador.',
  'g.reservationConflict':
    'No se pudo crear la reservación: la estación ya está ocupada o la persona ya tiene una reserva ese día.',
  // --- Asset map tooltip count ---------------------------------------------
  'map.assetCount.one': '· {count} activo',
  'map.assetCount.other': '· {count} activos',
  // --- Leftovers found by the widened guard --------------------------------
  'map.availableOf': 'de {total} estaciones disponibles',
  'skills.staleNote': 'Actualizado {since}',
  'skills.staleWorth': ' — vale la pena revisarlo',
  'reports.tab.lunch': 'Almuerzos',
  'reports.tab.deskbooking': 'Deskbooking',
  'reports.tab.assets': 'Activos',
  'reports.survey.completed': 'encuestas de almuerzo completadas {period}.',
  // --- Location groups and the last stragglers -----------------------------
  'locgroup.estacion': 'Estaciones',
  'locgroup.sala': 'Salas',
  'locgroup.gerencia': 'Gerencias',
  'locgroup.oficina': 'Oficinas',
  'locgroup.zonaComun': 'Zonas comunes',
  'locgroup.comedor': 'Comedor',
  'locgroup.departamento': 'Departamentos',
  'locgroup.bodega': 'Bodegas',
  'locgroup.mantenimiento': 'Mantenimiento',
  // --- Assets: disposal (dados de baja) ------------------------------------
  'assets.side.active': 'Activos',
  'assets.side.disposed': 'Dados de baja',
  'assets.disposed.title': 'Activos dados de baja',
  'assets.disposed.empty': 'No hay activos dados de baja con los filtros actuales.',
  'assets.col.disposedAt': 'Fecha de baja',
  'assets.col.disposalReason': 'Motivo',
  'assets.action.dispose': 'Dado de baja',
  'assets.action.restore': 'Restaurado',
  'assets.disposal.badge': 'Dado de baja',
  'assets.disposal.action': 'Dar de baja',
  'assets.disposal.restore': 'Restaurar',
  'assets.disposal.bannerLine': 'Dado de baja el {date} · {reason}',
  'assets.disposal.dialogTitle': 'Dar de baja el activo',
  'assets.disposal.dialogBody':
    'La placa {plate} dejará de aparecer en {location} y en los conteos del mapa. El registro se conserva y podés restaurarlo después.',
  'assets.disposal.reasonLabel': 'Motivo',
  'assets.disposal.noteLabel': 'Nota (opcional)',
  'assets.disposal.done': 'Activo dado de baja.',
  'assets.disposal.restored': 'Activo restaurado.',
  'assets.disposal.restoreTitle': 'Restaurar el activo',
  'assets.disposal.restoreBody':
    'La placa {plate} vuelve al inventario y aparecerá otra vez en {location}.',
  'assets.disposal.reason.nationalised': 'Nacionalizado',
  'assets.disposal.reason.damaged': 'Dañado',
  'assets.disposal.reason.obsolete': 'Obsoleto',
  'assets.disposal.reason.sold': 'Vendido',
  'assets.disposal.reason.lost': 'Perdido',
  'assets.disposal.reason.other': 'Otro',
  'assets.reports.disposed': 'Dados de baja',
  'assets.reports.downloadDisposedCsv': 'Descargar CSV',
  'booking.lunchCheckFailed':
    'No pudimos consultar la disponibilidad de almuerzo. Intentá de nuevo.',
  'admin.stationBlocked': 'Estación {station} bloqueada.',
  'admin.stationEnabled': 'Estación {station} habilitada.',
  // --- Office map cell labels ----------------------------------------------
  'loc.creativeManagement': 'Gerencia Creative',
  'loc.financeManagement': 'Gerencia Finanzas',
  'loc.generalManagement': 'Gerencia General',
  'loc.pmManagement': 'Gerencia PM',
  'loc.hrManagement': 'Gerencia RRHH',
  'loc.accountsManagement': 'Gerencia Accounts',
  'loc.hrOffices': 'Oficinas RRHH',
  'loc.itDepartment': 'Departamento IT',
  'loc.kitchenDining': 'Cocina y Comedor',
  'loc.tuanisRoom': 'Sala Tuanis',
  'loc.churchillRoom': 'Sala Churchill',
  'loc.laPulpeRoom': 'Sala La Pulpe',
  // Short form; 'loc.room506' already holds the full "Sala de Reunión 506".
  'loc.room506Short': 'Sala 506',
  'loc.nursingRoom': 'Sala de Lactancia',
  'loc.lockersHallway': 'Pasillo Lockers',
  'loc.managementHallway': 'Pasillo Gerencias',
  'admin.lunchSplit': 'Colaboradores: {people} · Admin: {admin}',
  'g.dateLabel': 'Fecha:',
  // --- Plural counts in the lunch and user panels --------------------------
  'admin.lunchCount.one': '{count} almuerzo',
  'admin.lunchCount.other': '{count} almuerzos',
  'admin.reassignedTo': 'Reasignado a {name}',
  // --- Actions menu on an administrative lunch -----------------------------
  'adminLunch.actions': 'Acciones del almuerzo de {name}',
  'adminLunch.reassign': 'Reasignar',
  'adminLunch.cancel': 'Cancelar almuerzo',
  'adminLunch.restore': 'Reactivar almuerzo',
  'adminLunch.cancelledBadge': 'Cancelado{time}',
  'adminLunch.cancelTitle': 'Cancelar almuerzo',
  'adminLunch.cancelQuestion': '¿Cancelar el almuerzo de {name}? Ya se pidió al proveedor, así que cuenta como cancelación tardía y queda disponible como almuerzo extra.',
  'adminLunch.cancelled': 'Almuerzo de {name} cancelado.',
  'adminLunch.restored': 'Almuerzo de {name} reactivado.',
  'admin.availableLunches.one': '{count} almuerzo disponible',
  'admin.availableLunches.other': '{count} almuerzos disponibles',
  'admin.lateCancelledCount.one': '{count} cancelado tarde',
  'admin.lateCancelledCount.other': '{count} cancelados tarde',
  'admin.alreadyAssigned.one': '{count} ya asignado',
  'admin.alreadyAssigned.other': '{count} ya asignados',
  'admin.userCount.one': '{count} usuario',
  'admin.userCount.other': '{count} usuarios',
  'admin.calculatingLunches': 'Calculando almuerzos disponibles…',
  // --- Skill Matrix: closed enums shown to people --------------------------
  'skl.level0': 'Sin conocimiento',
  'skl.level0Criteria':
    'No he utilizado la tecnología y tengo un conocimiento limitado de sus conceptos fundamentales.',
  'skl.level1': 'Básico',
  'skl.level1Criteria':
    'Comprendo los conceptos fundamentales y puedo realizar tareas sencillas siguiendo documentación y con acompañamiento.',
  'skl.level2': 'Intermedio',
  'skl.level2Criteria':
    'Trabajo de forma autónoma en las tareas del día a día. Resuelvo problemas habituales y sé dónde buscar información cuando se presenta una situación nueva.',
  'skl.level3': 'Avanzado',
  'skl.level3Criteria':
    'Diseño soluciones, tomo decisiones técnicas y brindo apoyo al equipo ante situaciones complejas. Puedo compartir conocimiento y acompañar a otros en su desarrollo.',
  'skl.disc.development': 'Desarrollo',
  'skl.disc.content': 'Contenido',
  'skl.disc.analysis': 'Análisis',
  'skl.disc.systems': 'Sistemas',
  'skl.disc.architecture': 'Arquitectura',
  'skl.lang.english': 'Inglés',
  'skl.lang.portuguese': 'Portugués',
  'skl.lang.french': 'Francés',
  'skl.lang.spanish': 'Español',
  'skl.lang.german': 'Alemán',
  'skl.lang.italian': 'Italiano',
  'skl.cefr.a1': 'A1 · Básico',
  'skl.cefr.a2': 'A2 · Elemental',
  'skl.cefr.b1': 'B1 · Intermedio',
  'skl.cefr.b2': 'B2 · Intermedio alto',
  'skl.cefr.c1': 'C1 · Avanzado',
  'skl.cefr.c2': 'C2 · Nativo o equivalente',
  // --- Projects panel and the last-updated indicator -----------------------
  'skills.projects': 'Proyectos',
  'skills.current': 'Actuales',
  'skills.past': 'Anteriores',
  'upd.seconds': 'Actualizado hace unos segundos',
  'upd.secondsN': 'Actualizado hace {count}s',
  'upd.minute': 'Actualizado hace 1 minuto',
  'upd.minutes': 'Actualizado hace {count} minutos',
  'upd.hour': 'Actualizado hace 1 hora',
  'upd.hours': 'Actualizado hace {count} horas',
  // --- Role and custody labels ---------------------------------------------
  'role.superAdmin': 'Super Admin',
  'role.deskbookingAdmin': 'Deskbooking',
  'role.lunchAdmin': 'Almuerzo',
  'role.assetAdmin': 'Activos Admin',
  'role.assetViewer': 'Activos Viewer',
  'role.reportsViewer': 'Reportes',
  'role.skillsAdmin': 'Skills Admin',
  'role.skillsViewer': 'Skills Tech',
  'custody.office': 'Oficina',
  'custody.employeeHome': 'Casa Colaborador',
  // --- Role assignment messages --------------------------------------------
  'admin.roleRemoved': 'Rol {role} removido de {email}.',
  'admin.roleAssigned': 'Rol {role} asignado a {email}.',
  'admin.revokeAria': 'Revocar {role}',
  // --- The last of the Spanish, found by scanning all of src ---------------
  'x.floorOccupancy': 'Ocupación del piso',
  'x.loading': 'Cargando',
  'x.reservationOf': 'Reservación de',
  'x.workstations': 'Estaciones de trabajo',
  'x.meetingRooms': 'Salas de reunión',
  'x.wentWrong': 'Algo salió mal',
  'x.unexpectedReload': 'Ha ocurrido un error inesperado. Por favor, recarga la página.',
  'x.reloadPage': 'Recargar página',
  'x.loadFailed': 'No pudimos cargar la información. Verificá tu conexión e intentá de nuevo.',
  'x.officeFull': 'Oficina llena',
  'x.noStationsFor': 'No hay estaciones disponibles para el',
  'x.willEmailYou': 'Te avisaremos por correo cuando se libere un campo.',
  'x.registering': 'Registrando...',
  'x.cancelling': 'Cancelando...',
  'x.occupancyHelp':
    'Establece el porcentaje del aforo diario de personas permitidas en las instalaciones.',
  'x.occupancyPercent': 'Porcentaje del aforo permitido',
  'x.bookWorkstation': 'Reservar estación de trabajo',
  'x.techFeatures': 'Características tecnológicas',
  'x.reservationDate': 'Fecha de reservación',
  'x.reservationTime': 'Hora de reservación',
  'x.chooseStation': 'Elegir estación',
  'x.bookStation': 'Reservar estación',
  'x.confirmReservation': 'Confirmar reservación',
  'x.redirectingOkta': 'Redireccionando a la página de inicio de sesión con okta',
  'x.maintenance': 'Estamos realizando mejoras en el sistema.',
  'x.workingOnIt': 'Trabajando en ello',
  'x.blockedUser': 'Su usuario se encuentra bloqueado temporalmente',
  'x.contactAdmin': 'Pongase en contacto con el administrador ¡Gracias!',
  'x.photoFormat': 'Formato no soportado. Solo PNG o JPG.',
  'x.photoSize': 'El archivo supera el límite de 5 MB.',
  'vf.verifyData': 'Por favor verifique que los datos autocompletados son correctos',
  'vf.fullName': 'Nombre Completo',
  'vf.phone': 'Número de Teléfono',
  'vf.travelled': '¿Ha viajado en los últimos 5 días fuera del país?',
  'vf.orangeAlert':
    '¿Ha viajado en los últimos 5 días a algún cantón bajo alerta naranja del país?',
  'vf.cantonVisited': 'Indique el cantón que visitó',
  'vf.returnDate': '¿En que fecha regresó?',
  'vf.closeContact':
    '¿Ha tenido algún contacto cercano a un caso sospechoso o confirmado por COVID-19?',
  'vf.symptoms':
    '¿Actualmente experimenta alguno de los siguientes síntomas similares a los del COVID-19?',
  'vf.vaccinated': '¿Tiene el esquema de vacunación completo?',
  'roled.superAdmin':
    'Acceso total a toda la plataforma. Incluye todos los demás roles, incluso los que se agreguen en el futuro.',
  'roled.deskbookingAdmin':
    'Administra reservaciones y puestos de trabajo. También entra a Control de Activos y Reportes.',
  'roled.lunchAdmin':
    'Administra el módulo de almuerzos: pedidos, validación y reportes de almuerzo.',
  'roled.assetAdmin':
    'Crea, edita y elimina activos en Control de Activos. Incluye sus reportes.',
  'roled.assetViewer':
    'Consulta Control de Activos y sus reportes. No puede crear ni modificar activos.',
  'roled.reportsViewer': 'Consulta la sección de Reportes. No administra nada.',
  'roled.skillsAdmin':
    'Administra el Skill Matrix: ve y edita todos los perfiles, el reporte de cobertura y agrega skills al catálogo.',
  'roled.skillsViewer':
    'Marca a una persona como técnica: puede ver y editar su propio perfil de skills. No ve los perfiles de los demás.',
  // --- Card and department line --------------------------------------------
  'x.reservationFor': 'Reservación de {name}',
  'x.department': 'Departamento: {name}',
  // --- Full-capacity modal and occupancy slider ----------------------------
  'x.noStationsOn': 'No hay estaciones disponibles para el',
  'x.dateLongFormat': 'dddd DD [de] MMMM',
  'x.occupancyHelpNoDot':
    'Establece el porcentaje del aforo diario de personas permitidas en las instalaciones',
  'x.station2G05': 'Estación 2G05',
  'x.from': 'De:',
  // --- Visit form and standalone pages -------------------------------------
  'vf.orangeAlertLong':
    '¿Ha viajado en los últimos 5 días a algún cantón bajo alerta naranja debido a un brote de COVID-19?',
  'vf.orangeAlertLink': 'Revisar cantones en alerta naranja',
  'vf.closeContactLong':
    '¿Ha tenido algún contacto cercano a un caso sospechoso o confirmado por COVID-19 en los últimos 14 días?',
  'vf.symptomsLong':
    '¿Actualmente experimenta alguno de los siguientes síntomas similares a los de la gripe? (fiebre, tos, falta de aire, falta de gusto, falta de olfato.)',
  'vf.declaration':
    'Me doy por enterado que toda la información suministrada por mi persona en este formulario es correcta, verdadera y completa.',
  'x.backSoon': 'Volveremos pronto.',

  // --- Skill Matrix: managers, freelancers, preview, countries (2026-10) ---
  'country.CR': 'Costa Rica',
  'country.GT': 'Guatemala',
  'g.prevArrow': '← Anteriores',
  'skills.anyCountry': 'Cualquier país',
  'skills.back': '← Volver',
  'skills.retry': 'Reintentar',
  'skills.loadFailed': 'No pudimos cargar el Skill Matrix. Revisá tu conexión e intentá de nuevo.',
  'skills.pageRange': '{from}–{to} de {total}',
  'skills.pastProjects': 'Experiencia previa',
  'skills.confirmDelete': '¿Seguro? Eliminar',
  'skills.stillCurrent': 'Siguen al día',
  'skills.newInGridDone': 'Listo, ya las revisé',
  'skills.field.manager': 'Manager',
  'skills.tab.directory': 'Directorio',
  'skills.tab.myTeam': 'Mi equipo',
  'skills.tab.teams': 'Equipos',
  'skills.project.end': 'Fin',
  'skills.add.createFailed': 'No se pudo crear la skill.',
  'skills.chart.peopleSeries': 'Personas',
  'skills.chart.persons.one': '{count} persona',
  'skills.chart.persons.other': '{count} personas',
  'skills.profile.country': 'País',
  'skills.profile.manager': 'Manager (correo)',
  'skills.profile.managerPlaceholder': 'nombre.apellido@vml.com',
  'skills.profile.freelanceEmailHint':
    'Con su correo VML entra directo a llenar su formulario de skills, sin ver nada más de la app.',
  'skills.create.title': 'Agregar persona',
  'skills.create.save': 'Agregar',
  'skills.create.email': 'Correo VML',
  'skills.create.emailHint': 'Con este correo la persona entra a su perfil. Podés dejarlo vacío y agregarlo después.',
  'skills.create.freelanceNeedsEmail': 'Un freelance necesita su correo VML para poder llenar el formulario.',
  'skills.create.rolesHint': 'Si no elegís, la persona elige su rol al empezar el formulario.',
  'skills.manager.title': 'Asignar manager',
  'skills.manager.assign': 'Asignar',
  'skills.manager.clear': 'Quitar el manager a estas personas',
  'skills.manager.emailRequired': 'Escribí el correo del manager.',
  'skills.manager.forPeople.one': 'Se asigna a {count} persona seleccionada.',
  'skills.manager.forPeople.other': 'Se asigna a las {count} personas seleccionadas.',
  'skills.dir.addPerson': '+ Agregar persona',
  'skills.dir.export': 'Exportar CSV',
  'skills.dir.anyManager': 'Cualquier manager',
  'skills.dir.showInactive': 'Incluir inactivos',
  'skills.dir.selectAll': 'Seleccionar todos los visibles',
  'skills.dir.select': 'Seleccionar a {name}',
  'skills.dir.selection': 'Personas seleccionadas',
  'skills.dir.selectedCount.one': '{count} seleccionada',
  'skills.dir.selectedCount.other': '{count} seleccionadas',
  'skills.dir.assignManager': 'Asignar manager',
  'skills.dir.clearSelection': 'Quitar selección',
  'skills.dir.noEmail': 'Sin correo',
  'skills.dir.noEmailTitle':
    'Sin un correo VML vinculado no puede entrar a llenar su formulario. Agregalo desde Editar.',
  'skills.ob.updateTitle': 'Actualizá tus skills',
  'skills.ob.updateIntro':
    'Ya están marcadas tus respuestas anteriores: cambiá solo lo que sea distinto y seguí.',
  'skills.ob.savedAsYouGo': 'Se guarda al avanzar.',
  'skills.fo.greeting': 'Skill Matrix · VML',
  'skills.fo.doneTitle': '¡Listo! Tus skills están guardadas',
  'skills.fo.doneTitleName': '¡Listo, {name}! Tus skills están guardadas',
  'skills.fo.doneText':
    'Gracias. Si algo cambia —aprendiste algo nuevo o subiste de nivel— volvé cuando quieras y actualizalo.',
  'skills.fo.savedNow': 'Guardamos tus respuestas. ¡Gracias por mantenerlas al día!',
  'skills.fo.update': 'Actualizar mis respuestas',
  'skills.fo.newSkills': 'Hay {count} skills nuevas para tu rol que todavía no respondiste.',
  'skills.fo.answerNew': 'Responderlas',
  'skills.fo.yourAnswers': 'Lo que respondiste',
  'skills.fo.lastUpdated': 'Última actualización: {date}',
  'skills.preview.enter': 'Ver como colaborador',
  'skills.preview.enterTitle': 'Mirá el Skill Matrix tal como lo ve una persona del equipo',
  'skills.preview.badge': 'Vista de colaborador',
  'skills.preview.note': 'Así ve el Skill Matrix una persona del equipo. Las opciones de admin están ocultas.',
  'skills.preview.exit': 'Volver a la vista de admin',
  'skills.preview.formTab': 'Formulario',
  'skills.preview.formBadge': 'Vista previa',
  'skills.preview.formNote': 'Nada de lo que marqués acá se guarda.',
  'skills.preview.changeRoles': 'Cambiar roles',
  'skills.preview.pickRoles': '¿Qué rol querés previsualizar?',
  'skills.preview.pickRolesHelp':
    'Elegí uno o varios roles y vas a ver exactamente las preguntas que recibe esa persona.',
  'skills.preview.endTitle': 'Así termina el formulario',
  'skills.preview.endText':
    'Al terminar, la persona entra al Skill Matrix y sus respuestas quedan en su perfil.',
  'skills.preview.again': 'Probar con otro rol',
  'skills.preview.noOwnProfile':
    'Tu cuenta no tiene un perfil de skills, así que no hay perfil propio para mostrar. Probá las pestañas Directorio o Formulario.',
  'skills.review.title': 'Revisión del manager',
  'skills.review.lastOn': 'Revisado el {date}',
  'skills.review.notDoneYet': 'Todavía no terminó el formulario; vas a poder revisarlo cuando lo complete.',
  'skills.review.markReviewed': 'Marcar como revisado',
  'skills.review.addNote': 'Agregar nota',
  'skills.review.editNote': 'Editar nota',
  'skills.review.save': 'Guardar y marcar revisado',
  'skills.review.placeholder':
    'Opcional: ¿algo que conversar? Por ejemplo, una skill que creés que está más alta o más baja.',
  'skills.review.fromManager': 'Nota de tu manager',
  'skills.team.people.one': '{count} persona',
  'skills.team.people.other': '{count} personas',
  'skills.team.pending': 'Sin completar',
  'skills.team.toReview': 'Por revisar',
  'skills.team.reviewed': 'Revisados',
  'skills.team.progress': 'Progreso',
  'skills.team.review': 'Revisión',
  'skills.team.actions': 'Acciones',
  'skills.team.doneOn': 'Completo · {date}',
  'skills.team.reviewedOn': 'Revisado · {date}',
  'skills.team.notReviewed': 'Sin revisar',
  'skills.team.changed': 'Cambió desde tu revisión',
  'skills.team.remind': 'Recordar',
  'skills.team.reminded': 'Le enviamos un recordatorio a {name}.',
  'skills.team.reAsk': 'Pedir actualización',
  'skills.team.reAskConfirm':
    '¿Pedirle a {name} que actualice sus skills? Va a ver de nuevo el formulario con sus respuestas actuales ya marcadas.',
  'skills.team.reAsked': 'Listo: {name} va a ver el formulario la próxima vez que entre.',
  'skills.team.open': 'Ver respuestas',
  'skills.team.openReview': 'Revisar',
  'skills.team.empty': 'Nadie tiene asignado este manager todavía.',
  'skills.team.noneInFilter': 'Nadie en este grupo. ¡Todo al día!',
  'skills.team.noManagers':
    'Todavía no hay managers asignados. Asignalos desde Colaboradores: seleccioná personas y usá "Asignar manager".',
  'skills.team.pickManager': 'Elegí un manager',
  'skills.team.adminHint': 'Ves exactamente lo mismo que ve ese manager.',
  'skills.team.teamsTitle': 'Escribirle a {name} por Teams',
  'skills.team.githubLabel': 'GitHub de {name}',

  // --- Skill Matrix: personal form link (no sign-in) -----------------------
  'skills.link.title': 'Enlace al formulario (sin iniciar sesión)',
  'skills.link.help':
    'Para quien no puede entrar a Deskbooking, como los freelance: abre una página donde responde solo sus skills. Es personal y vence a los 90 días.',
  'skills.link.none': 'Sin enlace',
  'skills.link.activeUntil': 'Activo hasta el {date}',
  'skills.link.send': 'Enviar por correo',
  'skills.link.sendTitle': 'Crea un enlace nuevo y lo envía a {email}',
  'skills.link.sent': 'Listo: le enviamos el enlace a {email}.',
  'skills.link.create': 'Crear enlace',
  'skills.link.newLink': 'Crear enlace nuevo',
  'skills.link.copy': 'Copiar',
  'skills.link.copied': '¡Copiado!',
  'skills.link.onceHint':
    'Copialo ahora: por seguridad no se puede volver a mostrar. Si lo perdés, creá uno nuevo.',
  'skills.link.replaceHint': 'Crear uno nuevo deja sin efecto el anterior.',
  'skills.link.revoke': 'Desactivar',
  'skills.link.revoked': 'El enlace dejó de funcionar.',

  // --- Skill Matrix: dashboard ---------------------------------------------
  'skills.tab.dashboard': 'Resumen',
  'skills.dash.showTable': 'Ver tabla',
  'skills.dash.showChart': 'Ver gráfico',
  'skills.dash.everyone': 'Todos los managers',
  'skills.dash.scopeAll': 'Toda la oficina',
  'skills.dash.scopeManager': 'El equipo de un manager',
  'skills.dash.scopeTeam': 'Tu equipo',
  'skills.dash.completion': 'Formulario completado',
  'skills.dash.doneOf': '{done} de {total} personas',
  'skills.dash.done': 'Completo',
  'skills.dash.inProgress': 'En progreso',
  'skills.dash.notStarted': 'Pendiente',
  'skills.dash.reviewedTile': 'Revisados por su manager',
  'skills.dash.ofDone': 'de {count} completos',
  'skills.dash.ofPeople': 'de {count} personas',
  'skills.dash.avgRated': 'Skills por persona, en promedio',
  'skills.dash.stale': 'Respuestas de hace más de un año',
  'skills.dash.withManager': 'Con manager asignado',
  'skills.dash.mismatch': 'Dicen que ningún rol les queda',
  'skills.dash.byRole': 'Avance por rol',
  'skills.dash.byRoleSub': 'Quien tiene dos roles cuenta en los dos.',
  'skills.dash.byCountry': 'Avance por país',
  'skills.dash.noRole': 'Sin rol todavía',
  'skills.dash.total': 'Total',
  'skills.dash.topSkills': 'Lo que más sabe el equipo',
  'skills.dash.topSkillsSub': 'Las skills con más personas, por nivel.',
  'skills.dash.gaps': 'Donde hay menos cobertura',
  'skills.dash.gapsSub':
    'De quienes completaron un formulario que pregunta la skill, qué parte la maneja en Intermedio o más.',
  'skills.dash.gapValue': '{strong} de {asked} en Intermedio o más',
  'skills.dash.asked': 'Preguntada a',
  'skills.dash.noGaps': 'Todavía no hay suficientes respuestas para ver brechas.',
  'skills.dash.noAnswers': 'Todavía nadie respondió.',
  'skills.dash.weekly': 'Ritmo de respuestas',
  'skills.dash.weeklySub': 'Formularios completados por semana, últimas 12 semanas.',
  'skills.dash.week': 'Semana del',
  'skills.dash.completed': 'Completados',
  'skills.dash.bonus': 'Plus (IA y otras)',
  'skills.dash.bonusSub': 'Entre las {count} personas que completaron el formulario.',
  'skills.dash.managers': 'Managers',
  'skills.dash.managersSub': 'Tocá un nombre para ver solo su equipo.',
  'skills.dash.people': 'Personas',
  'skills.dash.empty': 'No hay personas en esta selección.',

  // --- Skill Matrix: simplified layout (2026-10) ---------------------------
  'skills.tab.people': 'Personas',
  'skills.tab.search': 'Buscar gente',
  'skills.tab.config': 'Configuración',
  'skills.tab.mine': 'Mis skills',
  'skills.moreInfo': 'Más información',
  'skills.reviewAll': 'Repasar todo',
  'skills.state.pending': 'Pendientes',
  'skills.state.progress': 'En progreso',
  'skills.team.listTitle': 'Tu gente',
  'skills.config.roles': 'Roles y grillas',
  'skills.config.catalog': 'Catálogo de skills',
  'skills.search.addSkill': '+ Skill',
  'skills.search.available': 'Sin proyecto actual',
  'skills.search.minLevel': 'Nivel mínimo de {skill}',
  'skills.search.removeSkill': 'Quitar {skill}',
  'skills.over.coverage': 'Cobertura por skill',
  'skills.over.coverageHint': 'El detalle por categoría y rol',
  'skills.over.upkeep': 'Mantener las respuestas al día',
  'skills.over.upkeepHint':
    'Pedir actualización hace que la persona vea el formulario otra vez con sus respuestas ya marcadas: solo cambia lo que sea distinto. Nadie pierde lo que respondió.',
  'skills.over.stale': 'Respuestas viejas',
  'skills.over.byRole': 'Un rol entero',
  'skills.over.byRoleText': 'Útil después de cambiar la grilla de un rol.',
  'skills.over.pickRole': 'Elegí un rol',
  'skills.over.everybody': 'Todos',
  'skills.over.everybodyText': 'La actualización anual de toda la oficina.',
  'skills.launch.title': 'Antes de lanzar',
  'skills.launch.pending.one': 'Falta {count} cosa',
  'skills.launch.pending.other': 'Faltan {count} cosas',
  'skills.launch.allSet': 'Todo listo',
  'skills.launch.public': 'El módulo está abierto para toda la oficina',
  'skills.launch.access': '{done} de {total} personas pueden entrar',
  'skills.launch.accessHint':
    'Mientras el módulo no sea público, entra quien tenga acceso. Seleccioná personas en Personas y usá "Dar acceso", o pedí activar el módulo para todos (TALENT_MODULE_PUBLIC).',
  'skills.launch.alertsOn': 'Los recordatorios semanales están encendidos',
  'skills.launch.alertsOff': 'Los recordatorios semanales están apagados',
  'skills.launch.alertsHint':
    'Sin recordatorio, solo responde quien entra a la app por su cuenta. Se encienden en el servidor (SKILL_MATRIX_ALERTS_ENABLED); conviene hacerlo el día del lanzamiento.',
  'skills.launch.managers': '{done} de {total} personas tienen manager',
  'skills.launch.freelancers.one': '{count} freelance sin correo',
  'skills.launch.freelancers.other': '{count} freelance sin correo',
  'skills.launch.freelancersHint':
    'Sin correo no se les puede enviar el enlace. Agregalo desde su perfil, o descargá los enlaces y envialos vos.',
  'skills.launch.emptyRoles': 'Roles sin preguntas: {list}',
  'skills.launch.rolesOk': 'Todos los roles tienen preguntas',
  'skills.launch.goPeople': 'Ir a Personas',
  'skills.launch.goConfig': 'Ir a Configuración',
  'skills.launch.srDone': '(listo)',
  'skills.launch.srPending': '(pendiente)',
  'skills.dir.bulkRole': 'Agregar rol…',
  'skills.dir.bulkRoleDone': 'Listo: rol agregado a {done} personas ({skipped} ya lo tenían).',
  'skills.dir.bulkRemindDone': 'Enviamos {done} recordatorios. {skipped} se saltaron (sin correo, ya completo o recordado hace poco).',
  'skills.dir.bulkAccess': 'Dar acceso',
  'skills.dir.bulkAccessTitle': 'Les permite entrar al Skill Matrix antes de que sea público',
  'skills.dir.bulkAccessDone': 'Listo: {done} personas ya pueden entrar. {skipped} no tienen correo vinculado.',
  'skills.dir.bulkLinks': 'Descargar enlaces',
  'skills.dir.bulkLinksTitle': 'Un CSV con el enlace personal al formulario de cada persona (reemplaza los anteriores)',
  'skills.dir.bulkLinksDone': 'Descargamos {done} enlaces. Los anteriores de esas personas dejaron de funcionar.',
  'skills.cat.title': 'Catálogo de skills',
  'skills.cat.hint':
    'Acá se corrigen nombres, se retiran las que nadie usa y se unifican duplicadas. Unificar pasa los niveles de la gente a la que queda (gana el más alto) y retira la otra; nadie pierde respuestas.',
  'skills.cat.search': 'Buscar skill o categoría…',
  'skills.cat.show.all': 'Activas',
  'skills.cat.show.noGrid': 'En ninguna grilla',
  'skills.cat.show.unused': 'Sin uso',
  'skills.cat.show.inactive': 'Retiradas',
  'skills.cat.roles': 'Grillas',
  'skills.cat.inNoGrid': 'En ninguna grilla',
  'skills.cat.none': 'No hay skills en esta vista.',
  'skills.cat.merge': 'Unificar',
  'skills.cat.makeBonus': 'Hacer plus',
  'skills.cat.unBonus': 'Quitar del plus',
  'skills.cat.retire': 'Retirar',
  'skills.cat.restore': 'Reactivar',
  'skills.cat.shown.one': '{count} skill',
  'skills.cat.shown.other': '{count} skills',
  'skills.cat.editTitle': 'Editar skill',
  'skills.cat.name': 'Nombre',
  'skills.cat.editHint': 'Las {count} personas que la marcaron conservan su nivel.',
  'skills.cat.mergeTitle': 'Unificar «{name}»',
  'skills.cat.mergeInto': 'Pasar todo a',
  'skills.cat.mergeConfirm': 'Unificar',
  'skills.cat.mergeExplain':
    'Las {count} personas que marcaron «{from}» pasan a «{into}» con su nivel (si marcaron las dos, queda el más alto). «{from}» se retira.',
  'skills.rowMenu': 'Acciones para {name}',
  'skills.dir.moreFilters': 'Filtros',
  'quick.title': 'Smart Booking',
  'quick.headline': '{date} en {station}',
  'quick.lunch': 'Almuerzo {slot}',
  'quick.slot.12md-1pm': '12md a 1pm',
  'quick.slot.1pm-2pm': '1pm a 2pm',
  'quick.glutenFree': 'Sin gluten',
  'quick.noLunch': 'Sin almuerzo',
  'quick.lunchReason.FRIDAY': 'Sin almuerzo: no hay servicio los viernes',
  'quick.lunchReason.DAY_DISABLED': 'Sin almuerzo: no hay servicio ese día',
  'quick.lunchReason.CUTOFF': 'Sin almuerzo: ya cerró el pedido para ese día',
  'quick.lunchReason.FULL': 'Sin almuerzo: ya no quedan espacios',
  'quick.parking': 'Con parqueo',
  'quick.noParking': 'Sin parqueo',
  'quick.usualDay.1': 'Solés venir los lunes',
  'quick.usualDay.2': 'Solés venir los martes',
  'quick.usualDay.3': 'Solés venir los miércoles',
  'quick.usualDay.4': 'Solés venir los jueves',
  'quick.usualDay.5': 'Solés venir los viernes',
  'quick.dismiss': 'Ocultar Smart Booking',
  'quick.success': 'Listo: reservaste {station} para el {date}.',
  'quick.taken': 'Alguien tomó ese espacio hace un momento. Ya te buscamos otro.',
  'quick.failed': 'Smart Booking no pudo hacer la reserva. Probá desde el mapa.',
  'booking.prefilled': 'Precargado según tus últimas reservas. Podés cambiarlo.',
  'map.legend.presentIn': 'En la oficina',
  'map.legend.presentExpected': 'Aún no llega',
  'map.presence.in': 'En la oficina',
  'map.presence.expected': 'Aún no llega',
  'booking.stationAria.in': 'Estación {station}, ocupada; la persona ya está en la oficina',
  'booking.stationAria.expected': 'Estación {station}, ocupada; la persona aún no llega',
  'equipment.listLabel': 'Equipo de la estación',
  'equipment.report': 'Reportar un problema',
  'equipment.reportTitle': 'Problema en la estación {station}',
  'equipment.whichOne': '¿Con qué equipo?',
  'equipment.general': 'Con la estación en general',
  'equipment.whatHappens': '¿Qué pasa?',
  'equipment.placeholder': 'Por ejemplo: falta el monitor, el dock no carga',
  'equipment.send': 'Enviar reporte',
  'equipment.reportSent': 'Gracias. El equipo de activos ya recibió tu reporte.',
  'equipment.reportFailed': 'No se pudo enviar el reporte. Probá de nuevo en un momento.',
  'skills.search.inOffice': 'En la oficina ahora',
  'skills.search.inOfficeNow': 'En la oficina',
  'skills.search.inOfficeAt': 'En la oficina · {desk}',
  // --- Assets: inventory audit (barcode scan) ----------------------------
  'assets.audit.nav': 'Auditoría',
  'assets.audit.title': 'Auditorías de inventario',
  'assets.audit.subtitle': 'Recorra una ubicación escaneando las placas y compare lo que encuentra con lo registrado.',
  'assets.audit.new': 'Nueva auditoría',
  'assets.audit.empty': 'Todavía no hay auditorías.',
  'assets.audit.loadError': 'No se pudieron cargar las auditorías: {message}',
  'assets.audit.loadOneError': 'No se pudo cargar la auditoría: {message}',
  'assets.audit.notFound': 'Auditoría no encontrada.',
  'assets.audit.wholeOffice': 'Toda la oficina',
  'assets.audit.status.open': 'Abierta',
  'assets.audit.status.closed': 'Cerrada',
  'assets.audit.col.started': 'Inicio',
  'assets.audit.col.location': 'Ubicación',
  'assets.audit.col.status': 'Estado',
  'assets.audit.col.found': 'Encontrados',
  'assets.audit.col.missing': 'Faltantes',
  'assets.audit.col.startedBy': 'Iniciada por',
  'assets.audit.foundOf': '{found} de {expected}',
  'assets.audit.newBody': 'Elija qué va a recorrer. Toda la oficina espera cada activo de la oficina, salvo los que están en mantenimiento fuera.',
  'assets.audit.newLocation': 'Ubicación a auditar',
  'assets.audit.newNote': 'Nota (opcional)',
  'assets.audit.start': 'Iniciar',
  'assets.audit.back': 'Auditorías',
  'assets.audit.sessionTitle': 'Auditoría: {location}',
  'assets.audit.startedMeta': 'Iniciada el {date} por {email}',
  'assets.audit.closedMeta': 'Cerrada el {date} por {email}',
  'assets.audit.scanLabel': 'Escanee o escriba la placa y presione Enter',
  'assets.audit.scanPlaceholder': 'Placa del activo',
  'assets.audit.viewerHint': 'Solo el administrador de activos puede escanear en esta auditoría.',
  'assets.audit.camera': 'Escanear con cámara',
  'assets.audit.cameraHint': 'Apunte al código de barras de la placa. Cada código leído se registra solo.',
  'assets.audit.cameraError': 'No se pudo abrir la cámara: {message}',
  'assets.audit.cameraLast': 'Último código leído: {code}',
  'assets.audit.cameraDone': 'Listo',
  'assets.audit.counter.found': 'Encontrados',
  'assets.audit.counter.misplaced': 'En otra ubicación',
  'assets.audit.counter.unknown': 'Desconocidos',
  'assets.audit.counter.disposed': 'Dados de baja',
  'assets.audit.counter.missing': 'Faltantes',
  'assets.audit.counter.expected': 'Esperados',
  'assets.audit.counter.duplicate': 'Repetidos',
  'assets.audit.counters': 'Resumen de la auditoría',
  'assets.audit.result.found': 'Encontrado',
  'assets.audit.result.misplaced': 'En otra ubicación',
  'assets.audit.result.unknown': 'Desconocido',
  'assets.audit.result.disposed': 'Dado de baja',
  'assets.audit.result.duplicate': 'Repetido',
  'assets.audit.result.pending': 'Procesando',
  'assets.audit.result.failed': 'No registrado',
  'assets.audit.result.moved': 'Movido aquí',
  'assets.audit.recordedAt': 'Registrado en {place}',
  'assets.audit.atHome': 'casa del colaborador',
  'assets.audit.noAsset': 'Ninguna placa coincide con este código.',
  'assets.audit.move': 'Mover a esta ubicación',
  'assets.audit.moveTitle': 'Mover activo',
  'assets.audit.moveBody': '¿Mover {plate} de {from} a {to}? El cambio queda en el historial del activo.',
  'assets.audit.moveConfirm': 'Mover',
  'assets.audit.moved': '{plate} quedó registrado en {to}.',
  'assets.audit.scansTitle': 'Escaneos',
  'assets.audit.scansEmpty': 'Todavía no se ha escaneado nada.',
  'assets.audit.scanError': 'No se registró {code}: {message}',
  'assets.audit.missingTitle': 'Activos faltantes',
  'assets.audit.missingHint': 'Registrados aquí y todavía sin escanear.',
  'assets.audit.missingHintClosed': 'Registrados aquí y nunca escaneados durante la auditoría.',
  'assets.audit.missingEmpty': 'No falta ningún activo.',
  'assets.audit.close': 'Cerrar auditoría',
  'assets.audit.closeBody.one': 'Al cerrarla queda como registro y no admite más escaneos. {count} activo quedará como faltante.',
  'assets.audit.closeBody.other': 'Al cerrarla queda como registro y no admite más escaneos. {count} activos quedarán como faltantes.',
  'assets.audit.closedBanner': 'Auditoría cerrada: este es el reporte final.',
  'assets.audit.csv.type': 'Tipo',
  'assets.audit.csv.scan': 'Escaneo',
  'assets.audit.csv.missing': 'Faltante',
  'assets.audit.csv.code': 'Código escaneado',
  'assets.audit.csv.plate': 'Placa',
  'assets.audit.csv.result': 'Resultado',
  'assets.audit.csv.category': 'Categoría',
  'assets.audit.csv.brand': 'Marca',
  'assets.audit.csv.model': 'Modelo',
  'assets.audit.csv.recorded': 'Ubicación registrada',
  'assets.audit.csv.by': 'Escaneado por',
  'assets.audit.csv.at': 'Fecha',
  'assets.audit.csv.filename': 'auditoria-{id}-{date}.csv',
  'assets.issues.nav': 'Reportes de estaciones',
  'assets.issues.title': 'Reportes de estaciones',
  'assets.issues.subtitle': 'Problemas que reportó quien tenía reservada la estación. Cada uno también llega por correo al equipo de activos.',
  'assets.issues.filter': 'Mostrar reportes',
  'assets.issues.open': 'Abiertos',
  'assets.issues.resolved': 'Resueltos',
  'assets.issues.emptyOpen': 'No hay reportes abiertos.',
  'assets.issues.emptyResolved': 'Todavía no hay reportes resueltos.',
  'assets.issues.loadError': 'No se pudieron cargar los reportes.',
  'assets.issues.general': 'La estación en general',
  'assets.issues.reportedBy': 'Reportado por {name}',
  'assets.issues.resolvedBy': 'Resuelto por {name} el {date}',
  'assets.issues.resolve': 'Marcar como resuelto',
  'reports.desk.smartBooking': 'Con Smart Booking',
  'reports.desk.smartBookingDesc': '{pct}% de las reservas desde que se mide · {people} personas',
  'reports.desk.smartBookingSince': 'Se mide desde el 22 de octubre de 2026',
  'quick.thinking': 'Analizando tus últimas reservas…',
  'quick.why.FAVORITE': 'Es tu estación favorita y está libre ese día.',
  'quick.why.BESIDE_FAVORITE': '{favorite} está ocupada ese día, así que te propongo {station}, justo a la par.',
  'quick.why.FACING_FAVORITE': '{favorite} está ocupada ese día; {station} queda justo enfrente.',
  'quick.why.OTHER_FAVORITE': '{favorite} está ocupada ese día; {station} es otra de las que más usás.',
  'quick.why.NEAR_OTHER_FAVORITE': '{favorite} está ocupada ese día; {station} queda cerca de las que solés usar.',
  'quick.setup.same.DUAL_MONITOR': 'También tiene doble monitor.',
  'quick.setup.same.MONITOR': 'Tiene un monitor, como la tuya.',
  'quick.setup.same.HIGH_DESK': 'También es mesa alta.',
  'quick.setup.other.DUAL_MONITOR': 'Y tiene doble monitor.',
  'quick.setup.other.MONITOR': 'Ojo: tiene un solo monitor.',
  'quick.setup.other.HIGH_DESK': 'Ojo: es mesa alta, sin monitor.',
  'quick.otherOptions': 'Otras opciones:',
  'quick.alt.FAVORITE': 'tu favorita',
  'quick.alt.BESIDE_FAVORITE': 'a la par',
  'quick.alt.FACING_FAVORITE': 'enfrente',
  'quick.alt.OTHER_FAVORITE': 'frecuente',
  'quick.alt.NEAR_OTHER_FAVORITE': 'cerca',
  'quick.pickAria': 'Elegir {station}, {where}',
  'quick.basedOn.one': 'Según tu última reserva',
  'quick.basedOn.other': 'Según tus últimas {count} reservas',
  'extraLunch.selfAssigned': 'Autoasignado por chat',
  'assistant.more': 'Más ({count})',
  'assistant.adminLabel': 'Solo administradores',
  'assistant.qrEnlarge': 'Agrandar el QR',
  'assistant.languageToggle': 'Idioma del chat',
  'assistantIntro.title': 'Nuevo: tu asistente de reservas',
  'assistantIntro.subtitle': 'Es el robot de la esquina inferior izquierda. Escríbele como le hablarías a un compañero, o toca una opción, y resuelve esto sin pasar por las pantallas:',
  'assistantIntro.book.title': 'Reservar hablando',
  'assistantIntro.book.desc': 'Escritorio, almuerzo y parqueo en una frase.',
  'assistantIntro.book.example': '"Resérvame el jueves con almuerzo a la 1"',
  'assistantIntro.week.title': 'Reservar tu semana',
  'assistantIntro.week.desc': 'Tus días de siempre, esta semana o la próxima, con tu escritorio, almuerzo y parqueo. Te muestra la propuesta y confirmas.',
  'assistantIntro.week.example': '"Resérvame mi semana"',
  'assistantIntro.waitlist.title': 'Lista de espera automática',
  'assistantIntro.waitlist.desc': 'Si el día está lleno, te reserva apenas alguien cancele y te dice cuántos van antes.',
  'assistantIntro.waitlist.example': '"Ponme en lista de espera el viernes"',
  'assistantIntro.qr.title': 'Tu QR del almuerzo',
  'assistantIntro.qr.desc': 'Lo ves y lo agrandas en el chat.',
  'assistantIntro.qr.example': '"Dame mi QR"',
  'assistantIntro.leftovers.title': 'Almuerzos sobrantes',
  'assistantIntro.leftovers.desc': 'Si alguien canceló tarde, te asignas uno tú mismo.',
  'assistantIntro.leftovers.example': '"¿Hay almuerzos sobrantes?"',
  'assistantIntro.seat.title': 'Cambiar de asiento',
  'assistantIntro.seat.desc': 'Solo te ofrece escritorios libres, por fila.',
  'assistantIntro.seat.example': '"Muéveme a la fila C"',
  'assistantIntro.attendance.title': 'Tu asistencia',
  'assistantIntro.attendance.desc': 'A qué hora llegaste y cuántos días viniste.',
  'assistantIntro.attendance.example': '"¿Cuántos días vine este mes?"',
  'assistantIntro.language.title': 'Español o inglés',
  'assistantIntro.language.desc': 'Escríbele en cualquiera, o cámbialo arriba con ES | EN.',
  'assistantIntro.language.example': '"Speak English"',
  'assistantIntro.admin.title': 'Para administradores',
  'assistantIntro.admin.desc': 'Quién viene, almuerzos del día y del mes, reporte de cancelaciones tardías al correo, reservar o cancelar a otros. Sus opciones salen en dorado.',
  'assistantIntro.admin.example': '"¿Quién viene mañana?"',
  'assistantIntro.later': 'Después',
  'assistantIntro.try': 'Probar el chat',
  'assistant.open': 'Abrir el asistente de reservas',
  'assistant.close': 'Cerrar el asistente',
  'assistant.newChat': 'Nueva conversación',
  'assistant.title': 'Asistente de reservas',
  'assistant.subtitle': 'Escritorios, almuerzo y más',
  'assistant.welcome':
    'Hola, soy tu asistente de reservas. Puedo reservarte escritorio, cancelar o cambiar tus reservas, ajustar tu almuerzo, avisarte si hay almuerzos sobrantes y contarte cuándo viniste a la oficina. Escríbeme como quieras.',
  'assistant.suggest.bookTomorrow': 'Resérvame mañana',
  'assistant.suggest.myReservations': '¿Qué reservas tengo?',
  'assistant.suggest.leftovers': '¿Hay almuerzos sobrantes hoy?',
  'assistant.aiBadge': 'Nuevo',
  'assistant.greeting': '¿Te ayudo a reservar?',
  'assistant.footnote': 'Toca una opción o escríbelo como quieras. Los cambios se confirman por correo.',
  'assistant.suggest.attendance': '¿Cuántos días vine este mes?',
  'assistant.placeholder': 'Escribe tu mensaje',
  'assistant.send': 'Enviar',
  'assistant.you': 'Tú',
  'assistant.thinking': 'El asistente está escribiendo',
  'assistant.error': 'No pude conectar con el asistente. Intenta de nuevo en un momento.',
};
